import { useMemo, useState, useEffect, useCallback, useReducer, useRef } from "react";
import Swal from "sweetalert2";
import styles from "./Designer.module.css";
import ConfirmSideModal from "./ConfirmSideModal";
import DesignerVariantCatalogModal from "./DesignerVariantCatalogModal";
import DesignerMainLayout from "./DesignerMainLayout";
import DesignerColorQuickPickerModal from "./DesignerColorQuickPickerModal";
import useDesignerRemoteData from "./useDesignerRemoteData";
import { STEP, STEP_TITLES } from "./designerSteps";
import useDesignerDraftLifecycle from "./useDesignerDraftLifecycle";
import useDesignerSaveFlow from "./useDesignerSaveFlow";
import useDesignerResetFlow from "./useDesignerResetFlow";
import renderSketchImage from "../../utils/sketchRenderer";
import { PrintColorQuickSetupModal } from "./PartsSectionPrintModals";
import mcbRayitoLogo from "../../assets/designer/logo.png";
import {
    buildFullSelectionTree,
    buildAppliedPartsPreview,
    findNode,
    MASTER_PRINT_TYPE,
    MASTER_PART_TYPE,
    getPrintColorSlots,
    MASTER_PRINT_FONT_BASE_URL,
} from "../../utils/designerUtils";
import { resolvePlacementSide, coordToPct, LEGACY_BASE_SIZE } from "../../utils/designerGeometry";
import {
    createDesignerInitialState,
    designerReducer,
    DESIGNER_ACTIONS,
    buildTreeContext,
    getWizardPartNodes,
    getPartMaterialId,
    getSelectedPartColor,
    validatePrintSelection,
    collectBlockingIssues,
    filterPrintsForLocation,
} from "../../utils/designerState";
import {
    getAvailableColors,
    getMasterTemplateForOrderItem,
} from "../../services/api";
import {
    applyPredesignSizeAttrs,
    buildPrintOccupancyKey,
    normalizeColorObject,
    normalizeColorCode,
    buildPrintInstanceKey,
    buildSyntheticPrintForMode,
    getSelectionEffectiveSizeCm,
    getPrintLocationKeyFromSelection,
    isFixedPredesignSelection,
    resolvePrintPreviewUrl,
    resolvePrintSelectionSide,
    sanitizePrintLocationLabel,
    filterMaterialsForPart,
    shouldHideDesignerPart,
    hasRealPrintContent,
    roundUpToHalfCm,
} from "../../utils/designerHelpers";
import {
    adaptMasterTree,
    adaptAvailableColorsResponse,
} from "../../utils/designerAdapters";
import {
    selectPartCards,
    selectPartMaterialCards,
    selectPartMaterialCardByKey,
    selectPartGroups,
    selectResolvedActivePartKey,
    selectActivePartPrintLocations,
    selectStep2PrintLocations,
} from "../../utils/designerSelectors";
import {
    cmdSetActivePart,
    cmdSetActivePrint,
    cmdSetViewSide,
    cmdTogglePart,
    cmdSelectMaterial,
    cmdSelectColor,
    cmdClearColor,
    cmdSetPrintFilter,
    cmdSelectPrint,
    cmdSetPrintMode,
    cmdSetPrintText,
    cmdSetPrintImage,
    cmdSetPrintFont,
    cmdSetPrintColor,
    cmdMergePrintAttrs,
    cmdClearPrint,
} from "../../utils/designerCommands";
import {
    transitionWizardStep,
    WIZARD_EVENTS,
} from "../../utils/designerWizardMachine";
import {
    buildMirroredCustomCutState,
    normalizeCustomCutStroke,
    resolveCustomCutTargetGroupLabel,
    summarizeCustomCuts,
} from "../../utils/designerCustomCuts";

const IMAGE_LEGAL_DISCLAIMER = "Las imágenes cargadas y recortes aplicados están sujetos a aprobación de Little Blue.";
const DEFAULT_DEFAULT_COLOR_CODES = new Set(["#f9f7f7"]);
const STEP_BLOCKING_PREVIEW_COUNT = 4;
const PRINT_NEAREST_HOTSPOT_MIN_SWITCH_PCT = 0.8;
const PRINT_KIND = Object.freeze({
    TEXT: "text",
    PREDESIGN: "predesign",
    IMAGE_UPLOAD: "image_upload",
});
const TECHNIQUE_OPTIONS = [
    { id: MASTER_PRINT_TYPE.EMBROIDERY, label: "Bordado", key: "bordado" },
    { id: MASTER_PRINT_TYPE.PATCH, label: "Aplique", key: "aplique" },
    { id: MASTER_PRINT_TYPE.SERIGRAPHY, label: "Estampado", key: "estampado" },
];
const STRIPE_DRAW_DEFAULT_COLOR = "#1d4ed8";
const STRIPE_DRAW_DEFAULT_OPACITY = 0.94;
const STRIPE_JERSEY_MATERIAL_ID = 24;
const MAX_STRIPE_STROKES_PER_SIDE = 60;
const MAX_CUSTOM_CUTS_PER_SIDE = 48;
const PRINT_SPAWN_CENTER = Object.freeze({ leftPct: 50, topPct: 50 });

const normalizeStripePoint = (point = []) => ([
    Math.max(0, Math.min(1, Number(point?.[0] || 0))),
    Math.max(0, Math.min(1, Number(point?.[1] || 0))),
]);

const normalizeStripeStroke = (stroke = {}) => {
    const points = Array.isArray(stroke?.points_norm)
        ? stroke.points_norm.map((point) => normalizeStripePoint(point))
        : [];
    const rawMirrorScope = String(stroke?.mirror_scope || "").toLowerCase();
    const mirrorScope = rawMirrorScope === "sleeve" ? "sleeve" : "none";
    const rawMirroredFromSide = String(stroke?.mirrored_from_side || "").toLowerCase();
    const mirroredFromSide = rawMirroredFromSide === "front" || rawMirroredFromSide === "back"
        ? rawMirroredFromSide
        : null;
    const resolvedId = String(stroke?.id || `stripe-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const mirrorGroupId = String(stroke?.mirror_group_id || resolvedId);
    return {
        ...stroke,
        id: resolvedId,
        tool: String(stroke?.tool || "line") === "line" ? "line" : "curve",
        thickness: Math.max(2, Number(stroke?.thickness || 8)),
        opacity: Math.max(0.88, Math.min(1, Number(stroke?.opacity ?? STRIPE_DRAW_DEFAULT_OPACITY))),
        points_norm: points,
        mirror_scope: mirrorScope,
        mirror_group_id: mirrorGroupId,
        mirrored_from_side: mirroredFromSide,
    };
};

const mirrorStripeStroke = (stroke = {}, sourceSide = "front") => ({
    ...stroke,
    mirrored_from_side: sourceSide === "back" ? "back" : "front",
    points_norm: Array.isArray(stroke?.points_norm)
        ? stroke.points_norm.map((point) => {
            const [x, y] = normalizeStripePoint(point);
            return [Math.max(0, Math.min(1, 1 - x)), y];
        })
        : [],
});

const buildMirroredStripeState = (sourceSide, strokes, previousState = null) => {
    const safeSourceSide = sourceSide === "back" ? "back" : "front";
    const oppositeSide = safeSourceSide === "front" ? "back" : "front";
    const normalized = (Array.isArray(strokes) ? strokes : [])
        .map((stroke) => normalizeStripeStroke(stroke))
        .filter((stroke) => Array.isArray(stroke.points_norm) && stroke.points_norm.length >= 2)
        .slice(0, MAX_STRIPE_STROKES_PER_SIDE);
    const mirrored = normalized
        .filter((stroke) => stroke.mirror_scope === "sleeve" && !stroke.mirrored_from_side)
        .map((stroke) => mirrorStripeStroke(stroke, safeSourceSide));
    const mirroredIds = new Set(mirrored.map((stroke) => String(stroke?.id || "")));
    const previousOpposite = (Array.isArray(previousState?.[oppositeSide]) ? previousState[oppositeSide] : [])
        .map((stroke) => normalizeStripeStroke(stroke))
        .filter((stroke) => Array.isArray(stroke.points_norm) && stroke.points_norm.length >= 2);
    const preservedOpposite = previousOpposite.filter((stroke) => {
        if (String(stroke?.mirrored_from_side || "") === safeSourceSide) {
            return false;
        }
        if (mirroredIds.has(String(stroke?.id || ""))) {
            return false;
        }
        return true;
    });
    const nextOpposite = [...preservedOpposite, ...mirrored].slice(0, MAX_STRIPE_STROKES_PER_SIDE);
    return safeSourceSide === "front"
        ? { front: normalized, back: nextOpposite }
        : { front: nextOpposite, back: normalized };
};

const countUniqueStripeStrokes = (stripeDrawingsBySide = null) => {
    const ids = new Set();
    ["front", "back"].forEach((side) => {
        const list = Array.isArray(stripeDrawingsBySide?.[side]) ? stripeDrawingsBySide[side] : [];
        list.forEach((stroke) => {
            const id = String(stroke?.id || "").trim();
            if (id) ids.add(id);
        });
    });
    return ids.size;
};

const summarizeStripeStrokesByTool = (stripeDrawingsBySide = null) => {
    const byId = new Map();
    ["front", "back"].forEach((side) => {
        const list = Array.isArray(stripeDrawingsBySide?.[side]) ? stripeDrawingsBySide[side] : [];
        list.forEach((stroke) => {
            const id = String(stroke?.id || "").trim();
            if (!id || byId.has(id)) return;
            const tool = String(stroke?.tool || "line").toLowerCase() === "curve" ? "curve" : "line";
            byId.set(id, tool);
        });
    });
    let straight = 0;
    let curved = 0;
    byId.forEach((tool) => {
        if (tool === "curve") curved += 1;
        else straight += 1;
    });
    return {
        straight,
        curved,
        total: byId.size,
    };
};

const countUniqueCustomCuts = (customCutsBySide = null) => summarizeCustomCuts(customCutsBySide);

const normalizeCustomCutSummaryColor = (color = null) => {
    if (!color) return null;
    const normalized = normalizeColorObject(color);
    const code = String(normalized?.code || normalized?.hex || "").trim();
    const name = String(normalized?.name || "").trim();
    if (!code && !name) return null;
    return {
        id: normalized?.id ?? normalized?.color_id ?? null,
        color_id: normalized?.color_id ?? normalized?.id ?? null,
        code,
        name: name || code,
    };
};

const getCustomCutZoneOrder = (zoneId = "") => {
    const match = String(zoneId || "").match(/zone-(\d+)$/i);
    return match ? Number(match[1] || 0) : Number.MAX_SAFE_INTEGER;
};

const dedupeCustomCutSummaryColors = (colors = []) => {
    const seen = new Set();
    const list = [];
    (Array.isArray(colors) ? colors : []).forEach((color) => {
        const normalized = normalizeCustomCutSummaryColor(color);
        if (!normalized) return;
        const token = `${String(normalized.code || "").toLowerCase()}::${String(normalized.name || "").toLowerCase()}`;
        if (seen.has(token)) return;
        seen.add(token);
        list.push(normalized);
    });
    return list;
};

const getCustomCutZoneColorsForSide = (customCutZoneColors = {}, side = "front") =>
    dedupeCustomCutSummaryColors(
        Object.entries(customCutZoneColors && typeof customCutZoneColors === "object" ? customCutZoneColors : {})
            .filter(([zoneId]) => String(zoneId || "").includes(`-${side}-`))
            .sort(([leftId], [rightId]) => getCustomCutZoneOrder(leftId) - getCustomCutZoneOrder(rightId))
            .map(([, color]) => color)
    );

const pickCustomCutColorsForIndex = (colors = [], cutIndex = 0, cutCount = 0) => {
    const list = Array.isArray(colors) ? colors : [];
    if (!list.length) return [];
    if (list.length <= 2) return list;
    if (cutCount <= 1) return list.slice(0, 2);
    const maxStart = Math.max(0, list.length - 2);
    const ratio = cutCount <= 1 ? 0 : (cutIndex / Math.max(1, cutCount - 1));
    const start = Math.max(0, Math.min(maxStart, Math.round(ratio * maxStart)));
    return list.slice(start, start + 2);
};

const parsePrintConstraintNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getPrintCandidateSurface = (candidate = null) => {
    const width =
        parsePrintConstraintNumber(candidate?.max_width)
        || parsePrintConstraintNumber(candidate?.max_ancho)
        || parsePrintConstraintNumber(candidate?.maxw)
        || parsePrintConstraintNumber(candidate?.min_width)
        || parsePrintConstraintNumber(candidate?.min_ancho)
        || parsePrintConstraintNumber(candidate?.minw)
        || 0;
    const height =
        parsePrintConstraintNumber(candidate?.max_height)
        || parsePrintConstraintNumber(candidate?.min_height)
        || 0;
    return width > 0 && height > 0 ? width * height : 0;
};

const resolveBestBasePrintCandidate = ({
    candidates = [],
    mode = "image",
    isPersonalText = false,
} = {}) => {
    const list = (Array.isArray(candidates) ? candidates : []).filter((candidate) => Number(candidate?.id) > 0);
    if (!list.length) return null;

    const sorted = [...list].sort((left, right) => {
        const leftLetters = Number(left?.max_letters || 0);
        const rightLetters = Number(right?.max_letters || 0);
        const leftPersonal = Number(left?.personal || 0);
        const rightPersonal = Number(right?.personal || 0);
        const leftSurface = getPrintCandidateSurface(left);
        const rightSurface = getPrintCandidateSurface(right);

        if (mode === "text") {
            const personalTarget = isPersonalText ? 1 : 0;
            const leftPersonalScore = leftPersonal === personalTarget ? 1 : 0;
            const rightPersonalScore = rightPersonal === personalTarget ? 1 : 0;
            if (leftPersonalScore !== rightPersonalScore) {
                return rightPersonalScore - leftPersonalScore;
            }
            if (leftLetters !== rightLetters) {
                return rightLetters - leftLetters;
            }
        } else {
            if (leftLetters !== rightLetters) {
                return leftLetters - rightLetters;
            }
            if (leftSurface !== rightSurface) {
                return rightSurface - leftSurface;
            }
        }

        const leftHeight = parsePrintConstraintNumber(left?.max_height) || 0;
        const rightHeight = parsePrintConstraintNumber(right?.max_height) || 0;
        if (leftHeight !== rightHeight) {
            return rightHeight - leftHeight;
        }

        return Number(right?.id || 0) - Number(left?.id || 0);
    });

    return sorted[0] || null;
};

const buildCustomCutColorDetails = (customCutsBySide = null, customCutZoneColors = {}, customCutZonesBySide = null) => {
    const sideCutIds = {
        front: [],
        back: [],
    };

    ["front", "back"].forEach((side) => {
        const seen = new Set();
        const list = Array.isArray(customCutsBySide?.[side]) ? customCutsBySide[side] : [];
        list.forEach((rawCut) => {
            const cut = normalizeCustomCutStroke(rawCut);
            const id = String(cut?.id || "").trim();
            if (!id || seen.has(id)) return;
            seen.add(id);
            sideCutIds[side].push(id);
        });
    });

    const orderedCutIds = [];
    ["front", "back"].forEach((side) => {
        sideCutIds[side].forEach((id) => {
            if (!orderedCutIds.includes(id)) {
                orderedCutIds.push(id);
            }
        });
    });

    const colorsBySide = {
        front: getCustomCutZoneColorsForSide(customCutZoneColors, "front"),
        back: getCustomCutZoneColorsForSide(customCutZoneColors, "back"),
    };

    const zonesWithAdjacency = ["front", "back"].flatMap((side) => {
        const zones = Array.isArray(customCutZonesBySide?.[side]) ? customCutZonesBySide[side] : [];
        return zones
            .filter((zone) => Array.isArray(zone?.adjacentCutIds) && zone.adjacentCutIds.length > 0)
            .map((zone) => ({
                side,
                zone,
            }));
    });

    return orderedCutIds.map((cutId, index) => {
        const adjacentColors = dedupeCustomCutSummaryColors(
            zonesWithAdjacency
                .filter(({ zone }) => (zone.adjacentCutIds || []).includes(cutId))
                .map(({ zone }) => zone?.color)
        );
        if (adjacentColors.length > 0) {
            return {
                id: cutId,
                label: `Recorte ${index + 1}`,
                colors: adjacentColors,
                frontColors: adjacentColors,
                backColors: adjacentColors,
            };
        }
        const frontIndex = sideCutIds.front.indexOf(cutId);
        const backIndex = sideCutIds.back.indexOf(cutId);
        const frontColors = frontIndex >= 0
            ? pickCustomCutColorsForIndex(colorsBySide.front, frontIndex, sideCutIds.front.length)
            : [];
        const backColors = backIndex >= 0
            ? pickCustomCutColorsForIndex(colorsBySide.back, backIndex, sideCutIds.back.length)
            : [];
        return {
            id: cutId,
            label: `Recorte ${index + 1}`,
            colors: dedupeCustomCutSummaryColors([...frontColors, ...backColors]),
            frontColors,
            backColors,
        };
    });
};

const buildLightweightCustomCutZones = (zones = []) =>
    (Array.isArray(zones) ? zones : []).map((zone) => ({
        id: String(zone?.id || ""),
        label: String(zone?.label || "Zona"),
        side: String(zone?.side || "front") === "back" ? "back" : "front",
        targetGroup: String(zone?.targetGroup || "free") === "sleeve"
            ? "sleeve"
            : (String(zone?.targetGroup || "free") === "body" ? "body" : "free"),
        componentKey: String(zone?.componentKey || ""),
        adjacentCutIds: Array.isArray(zone?.adjacentCutIds)
            ? zone.adjacentCutIds.map((cutId) => String(cutId || "")).filter(Boolean)
            : [],
        color: zone?.color
            ? {
                id: zone.color.id ?? zone.color.color_id ?? null,
                color_id: zone.color.color_id ?? zone.color.id ?? null,
                code: String(zone.color.code || ""),
                name: String(zone.color.name || "Color"),
            }
            : null,
    }));

const resolveMirroredCustomCutZoneId = (zoneId) => {
    const value = String(zoneId || "").trim();
    if (!value) return "";
    if (value.includes("-front-")) return value.replace("-front-", "-back-");
    if (value.includes("-back-")) return value.replace("-back-", "-front-");
    return "";
};

const areLightweightCustomZonesEqual = (leftZones = [], rightZones = []) => {
    if (leftZones === rightZones) return true;
    if (!Array.isArray(leftZones) || !Array.isArray(rightZones)) return false;
    if (leftZones.length !== rightZones.length) return false;
    for (let index = 0; index < leftZones.length; index += 1) {
        const left = leftZones[index] || {};
        const right = rightZones[index] || {};
        if (
            String(left.id || "") !== String(right.id || "")
            || String(left.label || "") !== String(right.label || "")
            || String(left.side || "") !== String(right.side || "")
            || String(left.targetGroup || "") !== String(right.targetGroup || "")
            || String(left.componentKey || "") !== String(right.componentKey || "")
            || JSON.stringify(left.adjacentCutIds || []) !== JSON.stringify(right.adjacentCutIds || [])
            || String(left?.color?.code || "") !== String(right?.color?.code || "")
            || String(left?.color?.name || "") !== String(right?.color?.name || "")
            || String(left?.color?.id ?? left?.color?.color_id ?? "") !== String(right?.color?.id ?? right?.color?.color_id ?? "")
        ) {
            return false;
        }
    }
    return true;
};

const findDefaultPartColor = (colors = []) =>
    (colors || []).find((color) =>
        DEFAULT_DEFAULT_COLOR_CODES.has(String(normalizeColorCode(color?.code) || "").toLowerCase())
    ) || null;

// STEP / STEP_TITLES viven en ./designerSteps (compartidos con DesignerStepPanel).

const escapeHtml = (value) =>
    String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");

const resolvePrintTechniqueLabel = (selection = null) => {
    const attrs = selection?.attrs || {};
    const resolvedTechnique = String(attrs.resolved_technique || "").toLowerCase();
    if (resolvedTechnique === "aplique") return "Aplique";
    if (resolvedTechnique === "estampado" || resolvedTechnique === "serigrafia" || resolvedTechnique === "serigrafía") return "Estampado";
    const fallbackType = Number(attrs.resolved_master_print_type || selection?.print?.master_print_type || 0);
    if (fallbackType === Number(MASTER_PRINT_TYPE.PATCH)) return "Aplique";
    if (fallbackType === Number(MASTER_PRINT_TYPE.SERIGRAPHY)) return "Estampado";
    return "Bordado";
};

const buildBlockingListHtml = (sections = [], previewCount = STEP_BLOCKING_PREVIEW_COUNT) => {
    const normalizeSectionTitle = (rawTitle) => {
        const clean = String(rawTitle || "").trim();
        if (!clean) return "General";
        const normalized = clean.toLowerCase();
        if (normalized === "master" || normalized === "parte master" || normalized === "master part") {
            return "Base";
        }
        return clean;
    };
    const normalizedSections = (sections || [])
        .map((section) => ({
            title: normalizeSectionTitle(section?.title),
            items: Array.from(
                new Set((section?.items || []).map((item) => String(item || "").trim()).filter(Boolean))
            ),
        }))
        .filter((section) => section.items.length > 0);
    if (!normalizedSections.length) return "";

    const sectionsHtml = normalizedSections.map((section) => {
        const visible = section.items.slice(0, previewCount);
        const hidden = section.items.slice(previewCount);
        const visibleHtml = visible
            .map((message) => `<li style="margin: 0 0 6px;">${escapeHtml(message)}</li>`)
            .join("");
        const hiddenHtml = hidden.length
            ? `
            <details style="margin-top: 8px; text-align: left;">
                <summary style="cursor: pointer; color: #0071ce; font-weight: 700;">Ver más...</summary>
                <ul style="margin: 8px 0 0 18px; padding: 0; max-height: 160px; overflow: auto;">
                    ${hidden
                        .map((message) => `<li style="margin: 0 0 6px;">${escapeHtml(message)}</li>`)
                        .join("")}
                </ul>
            </details>
            `
            : "";
        return `
            <section style="margin: 0 0 10px;">
                <p style="margin: 0 0 6px; font-weight: 700; color: #1f3658;">${escapeHtml(section.title)}</p>
                <ul style="margin: 0 0 0 18px; padding: 0;">
                    ${visibleHtml}
                </ul>
                ${hiddenHtml}
            </section>
        `;
    }).join("");

    return `
        <div style="text-align: left;">
            <p style="margin: 0 0 10px;">Completá estos puntos antes de continuar:</p>
            ${sectionsHtml}
        </div>
    `;
};

const Designer = ({
    isOpen = false,
    onClose,
    masterTree,
    orderItemId,
    bSide = 0,
    productModelId = null,
    variants = [],
    switchingVariant = false,
    variantError = "",
    onSelectVariant = null,
    customerId = null,
    variant = "modal",
}) => {
    const isPageVariant = variant === "page";
    const isDesignerOpen = isPageVariant ? true : !!isOpen;
    const variantOptions = useMemo(
        () => (
            Array.isArray(variants)
                ? variants.filter(
                    (item) =>
                        !item?.selected
                        && Number(item?.catalog_eligible ?? 1) === 1
                )
                : []
        ),
        [variants]
    );
    const activeVariantName = useMemo(
        () => variants.find((item) => item.selected)?.name || "Sin variante seleccionada",
        [variants]
    );
    // Categoría de la prenda (order_item_category_id) del variant activo → define la técnica del
    // print (remera=estampado; buzo/campera/pantalón=bordado→aplique por tamaño). Ver PrintWizard.
    const printCategoryId = useMemo(() => {
        const list = Array.isArray(variants) ? variants : [];
        const byModel = list.find((v) => Number(v?.product_model_id) === Number(productModelId));
        const sel = byModel || list.find((v) => v?.selected) || list[0] || null;
        return sel?.category_id != null ? Number(sel.category_id) : null;
    }, [variants, productModelId]);
    const [state, dispatch] = useReducer(designerReducer, undefined, createDesignerInitialState);
    const [currentStep, setCurrentStep] = useState(1);

    const [colorOptions, setColorOptions] = useState({});
    const [loadingColors, setLoadingColors] = useState({});
    const [loadingMaterialColors, setLoadingMaterialColors] = useState({});
    const [expandedPalettes, setExpandedPalettes] = useState({});
    const [materialResolutionByPart, setMaterialResolutionByPart] = useState({});
    const [sideConfirm, setSideConfirm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState(null);
    const [summaryPdfUrl, setSummaryPdfUrl] = useState("");
    const [sketchGenerating] = useState(false);
    const [sketchImage, setSketchImage] = useState(null);
    const [sketchError, setSketchError] = useState(null);
    // Preview frente+espalda (lado a lado) que se muestra ARRIBA del resumen en el paso final.
    const [finalPreviewImage, setFinalPreviewImage] = useState(null);
    const [finalPreviewLoading, setFinalPreviewLoading] = useState(false);
    const [printHotspotFilter, setPrintHotspotFilter] = useState(null);
    const [showPrintHotspots, setShowPrintHotspots] = useState(false);
    const [printAdvisory, setPrintAdvisory] = useState(null);
    const [printColorQuickSetup, setPrintColorQuickSetup] = useState(null);
    const [showVariantCatalog, setShowVariantCatalog] = useState(false);
    // Modo de diseño: "proposed" = usa NUESTROS recortes (no editables salvo opt-in) ·
    // "custom" = «Diseño personalizado», recortes libres desde el arranque.
    const [designMode, setDesignMode] = useState("proposed");
    // Opt-in de recortes en modo propuesto ("¿agregar un recorte extra?").
    const [customCutOptIn, setCustomCutOptIn] = useState(false);
    // Color BASE de la prenda: se pinta todo de este color y después se corrige por parte.
    const [baseColor, setBaseColor] = useState(null);
    const [isPreviewCollapsed, setIsPreviewCollapsed] = useState(false);
    const [isFooterNavCollapsed, setIsFooterNavCollapsed] = useState(false);
    const [activePartSectionKey, setActivePartSectionKey] = useState("");
    const [colorQuickPicker, setColorQuickPicker] = useState(null);
    const [partSparkleFx, setPartSparkleFx] = useState({ keys: [], token: 0 });
    const [customCutSparkleFx, setCustomCutSparkleFx] = useState({ ids: [], token: 0 });
    const [dualPaintByPair, setDualPaintByPair] = useState({});
    const [stripeDrawingsBySide, setStripeDrawingsBySide] = useState({ front: [], back: [] });
    const [stripeDrawEditor, setStripeDrawEditor] = useState({
        enabled: false,
        tool: "none",
        color: STRIPE_DRAW_DEFAULT_COLOR,
        thickness: 8,
        opacity: STRIPE_DRAW_DEFAULT_OPACITY,
    });
    const [customCutsBySide, setCustomCutsBySide] = useState({ front: [], back: [] });
    const [customCutZoneColors, setCustomCutZoneColors] = useState({});
    const [customCutZonesBySide, setCustomCutZonesBySide] = useState({ front: [], back: [] });
    const [customCutEditor, setCustomCutEditor] = useState({
        enabled: false,
        tool: "none",
    });
    const [datasheetGuard, setDatasheetGuard] = useState({
        loading: false,
        canCreate: true,
        message: null,
    });
    const materialColorPromisesRef = useRef({});
    const colorOptionsRef = useRef({});
    const autoMaterialAttemptRef = useRef({});
    const autoWhiteColorAttemptRef = useRef({});
    const advisoryAlertKeyRef = useRef(null);
    const advisoryVisibleKeyRef = useRef(null);
    const advisoryWarningHitsRef = useRef({});
    const advisoryWarningShownRef = useRef({});
    const advisoryWarningAutoSwitchRef = useRef({});
    const printClipAlertCooldownRef = useRef(0);
    const printColorQuickSetupResolverRef = useRef(null);
    const customCutRuntimeBySideRef = useRef({ front: null, back: null });

    const normalizedRoot = useMemo(() => adaptMasterTree(masterTree), [masterTree]);
    const treeContext = useMemo(() => buildTreeContext(normalizedRoot), [normalizedRoot]);

    // Grupos de variantes "Tipo de parte" (master_part_type 7) presentes en el modelo.
    // Estable por modelo (no depende de la selección) → define si aparece el paso "Cambiar detalles".
    const modelHasDetailVariants = useMemo(
        () =>
            Object.values(treeContext.exclusiveGroups || {}).some(
                (group) => Number(group.type) === Number(MASTER_PART_TYPE.PART_TYPE)
            ),
        [treeContext]
    );

    // Pasos del wizard. "Cambiar detalles" se inserta solo si el modelo tiene variantes tipo 7.
    // Los ids se recalculan contiguos tras filtrar, para que el Stepper numere bien.
    const steps = useMemo(() => {
        const defs = [
            { key: STEP.MODEL },
            modelHasDetailVariants ? { key: STEP.DETAILS } : null,
            { key: STEP.PAINT },
            { key: STEP.STRIPES },
            { key: STEP.PRINTS },
            { key: STEP.SUMMARY },
        ].filter(Boolean);
        return defs.map((step, index) => ({
            ...step,
            id: index + 1,
            title: STEP_TITLES[step.key],
        }));
    }, [modelHasDetailVariants]);

    const currentStepKey = useMemo(
        () => steps.find((step) => step.id === currentStep)?.key || STEP.MODEL,
        [steps, currentStep]
    );

    // Grupos de variantes "Tipo de parte" (7) para el paso "Cambiar detalles": cada grupo es un
    // conjunto de opciones excluyentes (radio) bajo una parte del modelo que está seleccionada.
    const detailVariantGroups = useMemo(() => {
        return Object.values(treeContext.exclusiveGroups || {})
            .filter((group) => Number(group.type) === Number(MASTER_PART_TYPE.PART_TYPE))
            .filter((group) => {
                const parentKey = String(group.parentKey || "");
                return !parentKey || parentKey === "master" || state.selectedKeys.has(parentKey);
            })
            .map((group) => {
                const parentNode = treeContext.nodeByKey[String(group.parentKey)];
                const parentLabel = parentNode?.Item?.nname || parentNode?.Item?.name || "Detalle";
                const options = group.keys.map((key) => {
                    const node = treeContext.nodeByKey[String(key)];
                    return {
                        key: String(key),
                        label: node?.Item?.nname || node?.Item?.name || "Opción",
                        icon: node?.Item?.img_icon || null,
                        selected: state.selectedKeys.has(String(key)),
                    };
                });
                const selectedKey = options.find((option) => option.selected)?.key || null;
                return {
                    id: group.id,
                    parentKey: String(group.parentKey),
                    parentLabel,
                    required: (group.requiredKeys?.size || 0) > 0,
                    options,
                    selectedKey,
                };
            })
            .filter((group) => group.options.length > 0);
    }, [treeContext, state.selectedKeys]);

    const {
        allColors,
        printDetails,
        littlePredesigns,
        littlePredesignsLoading,
        littlePredesignsLoaded,
        designFontsLoaded,
        loadLittlePredesigns,
        preloadPrintDetailsByIds,
    } = useDesignerRemoteData({
        isDesignerOpen,
        isPrintsStep: currentStepKey === STEP.PRINTS,
        selectedPrints: state.selectedPrints,
    });


    // Única fuente de verdad para la base de las tipografías (V4, ver designerUtils). Antes este
    // cargador usaba same-origin y PreviewSection usaba otra base → podían cargar juegos distintos.
    const fontBaseUrl = MASTER_PRINT_FONT_BASE_URL;

    useEffect(() => {
        colorOptionsRef.current = colorOptions;
    }, [colorOptions]);

    const { draftStorageKey } = useDesignerDraftLifecycle({
        isDesignerOpen,
        isPageVariant,
        normalizedRoot,
        treeContext,
        orderItemId,
        bSide,
        productModelId,
        dispatch,
        state,
        currentStep,
        totalSteps: steps.length,
        setCurrentStep,
        setSaveMsg,
        setSketchError,
        setSketchImage,
        setExpandedPalettes,
        setPrintHotspotFilter,
        setShowPrintHotspots,
        setSideConfirm,
        setStripeDrawingsBySide,
        stripeDrawingsBySide,
        setCustomCutsBySide,
        customCutsBySide,
        setCustomCutZoneColors,
        customCutZoneColors,
        setCustomCutEditor,
        activePartSectionKey,
        setActivePartSectionKey,
    });

    useEffect(() => {
        if (!isDesignerOpen || !orderItemId) return;
        let cancelled = false;
        setDatasheetGuard((prev) => ({
            ...prev,
            loading: true,
            message: null,
        }));

        getMasterTemplateForOrderItem(orderItemId, bSide)
            .then((response) => {
                if (cancelled) return;
                const canCreate = response?.can_create !== undefined
                    ? !!response.can_create
                    : !response?.exists;
                setDatasheetGuard({
                    loading: false,
                    canCreate,
                    message: canCreate
                        ? null
                        : "Ya existe una ficha activa para esta prenda. Podrás guardar una nueva cuando Little Blue la revise.",
                });
            })
            .catch(() => {
                if (cancelled) return;
                setDatasheetGuard({
                    loading: false,
                    canCreate: true,
                    message: null,
                });
            });

        return () => {
            cancelled = true;
        };
    }, [isDesignerOpen, orderItemId, bSide]);

    const partNodes = useMemo(
        () => getWizardPartNodes({ root: normalizedRoot, context: treeContext, selectedKeys: state.selectedKeys }),
        [normalizedRoot, treeContext, state.selectedKeys]
    );

    const partCards = useMemo(
        () =>
            selectPartCards({
                partNodes,
                treeContext,
                state,
                allColors,
            }),
        [partNodes, treeContext, state, allColors]
    );

    useEffect(() => {
        if (!partCards.length) return;
        const activeExists = partCards.some((part) => part.key === state.activePartKey && part.selected);
        if (activeExists) return;
        const fallback = partCards.find((part) => part.selected)?.key || partCards[0].key;
        if (fallback && fallback !== state.activePartKey) {
            dispatch({ type: DESIGNER_ACTIONS.SET_ACTIVE_PART, partKey: fallback });
        }
    }, [partCards, state.activePartKey]);


    const allPrintLocations = useMemo(() => {
        if (!normalizedRoot) return [];
        const locations = [];
        const walk = (node, partKey = null, partNode = null) => {
            if (!node) return;
            const key = String(node.key);
            const isPrintNode = Number(node.type) === 6;

            if (!isPrintNode && key !== "master") {
                partKey = key;
                partNode = node;
            }

            if (isPrintNode && node?.Item?.display !== "none" && partKey && state.selectedKeys.has(partKey)) {
                locations.push({
                    key,
                    node,
                    partKey,
                    partLabel: partNode?.Item?.nname || partNode?.Item?.name || "Parte",
                });
            }

            (node.children || []).forEach((child) => walk(child, partKey, partNode));
        };
        walk(normalizedRoot);
        return locations;
    }, [normalizedRoot, state.selectedKeys]);

    const locationSublimationByKey = useMemo(() => {
        const map = {};
        allPrintLocations.forEach((location) => {
            map[String(location.key)] = false;
        });
        return map;
    }, [allPrintLocations]);

    const partHotspots = useMemo(() => {
        const parseCoord = (value) => {
            if (value === null || value === undefined) return null;
            if (typeof value === "string") {
                const raw = value.trim();
                if (!raw) return null;
                if (raw.endsWith("%")) {
                    const pct = Number(raw.slice(0, -1).trim());
                    return Number.isFinite(pct) ? `${pct}%` : null;
                }
                if (raw.toLowerCase().endsWith("px")) {
                    const px = Number(raw.slice(0, -2).trim());
                    return Number.isFinite(px) ? px : null;
                }
                const numeric = Number(raw);
                return Number.isFinite(numeric) ? numeric : null;
            }
            const numeric = Number(value);
            return Number.isFinite(numeric) ? numeric : null;
        };
        const resolvePrintLocationSide = (node) => {
            if (!node || Number(node.type) !== 6) return null;
            const item = node?.Item || {};
            return resolvePlacementSide(item?.placement ?? item?.posicion ?? null);
        };

        return allPrintLocations
            .map((location) => {
                const key = String(location?.key || "");
                const partKey = String(location?.partKey || "");
                const node = location?.node || null;
                const item = node?.Item || {};
                const top = parseCoord(item.top);
                const left = parseCoord(item.left);
                if (!key || !partKey || top === null || left === null) return null;
                const side = resolvePrintLocationSide(node);
                if (!side) return null;
                const niceName = node?.master_parts?.nice_name
                    || node?.master_part?.nice_name
                    || node?.masterPart?.nice_name
                    || item?.nname
                    || item?.nice_name
                    || node?.nice_name
                    || item?.name
                    || "Diseño";

                return {
                    key,
                    displayLabel: sanitizePrintLocationLabel(niceName),
                    label: sanitizePrintLocationLabel(item?.nname || item?.name, "Ubicación de diseño"),
                    partKey,
                    partLabel: sanitizePrintLocationLabel(location?.partLabel, "Parte"),
                    top,
                    left,
                    side,
                    selected: String(getPrintLocationKeyFromSelection(state.activePrintKey)) === key,
                };
            })
            .filter(Boolean);
    }, [
        allPrintLocations,
        state.activePrintKey,
    ]);

    const printLocationMetaByKey = useMemo(() => {
        const map = {};
        (allPrintLocations || []).forEach((location) => {
            const key = String(location?.key || "");
            if (!key) return;
            map[key] = {
                partLabel: sanitizePrintLocationLabel(location?.partLabel, "Base"),
            };
        });
        return map;
    }, [allPrintLocations]);

    const printHotspotMetrics = useMemo(() => {
        const toPct = (value) => {
            const pct = coordToPct(value, LEGACY_BASE_SIZE, LEGACY_BASE_SIZE);
            return Number.isFinite(pct) ? pct : null;
        };
        const map = {};
        const list = (partHotspots || [])
            .map((spot) => {
                const leftPct = toPct(spot.left);
                const topPct = toPct(spot.top);
                if (!Number.isFinite(leftPct) || !Number.isFinite(topPct)) return null;
                return {
                    ...spot,
                    leftPct,
                    topPct,
                };
            })
            .filter(Boolean);
        list.forEach((spot) => {
            map[String(spot.key)] = spot;
        });
        return { list, map };
    }, [partHotspots]);

    useEffect(() => {
        if (currentStepKey !== STEP.PRINTS) return;
        if (!allPrintLocations.length) {
            if (state.activePrintKey) {
                dispatch({ type: DESIGNER_ACTIONS.SET_ACTIVE_PRINT, printKey: null });
            }
            return;
        }
        const activeLocationKey = getPrintLocationKeyFromSelection(state.activePrintKey);
        const exists = allPrintLocations.some((location) => String(location.key) === String(activeLocationKey));
        if (!exists) {
            dispatch({ type: DESIGNER_ACTIONS.SET_ACTIVE_PRINT, printKey: null });
        }
    }, [currentStepKey, allPrintLocations, state.activePrintKey, state.selectedPrints]);

    const activePrintSelection = state.activePrintKey ? state.selectedPrints[String(state.activePrintKey)] || null : null;
    const occupiedPrintHotspots = useMemo(() => {
        const next = new Map();
        Object.entries(state.selectedPrints || {}).forEach(([printKey, selection]) => {
            if (!selection?.print) return;
            const locationKey = String(getPrintLocationKeyFromSelection(printKey, selection));
            if (!locationKey) return;
            const hotspot = printHotspotMetrics.map[locationKey] || null;
            const side = resolvePrintSelectionSide(selection, hotspot?.side || null);
            if (!side) return;
            const occupancyKey = buildPrintOccupancyKey(locationKey, side);
            if (!next.has(occupancyKey)) next.set(occupancyKey, new Set());
            next.get(occupancyKey).add(String(printKey));
        });
        return next;
    }, [state.selectedPrints, printHotspotMetrics]);
    const allPrintSelections = useMemo(
        () =>
            Object.entries(state.selectedPrints || {})
                .map(([printKey, selection]) => ({ printKey: String(printKey), selection }))
                .filter((entry) => !!entry.selection?.print),
        [state.selectedPrints]
    );

    useEffect(() => {
        if (!activePrintSelection?.print) {
            setPrintAdvisory(null);
            return;
        }
        // Escudo de posición fija: técnica/tamaño/ubicación ya vienen definidos del ABM → sin avisos.
        if (Number.isFinite(Number(activePrintSelection?.attrs?.abs_left_pct))) {
            setPrintAdvisory(null);
            return;
        }
        const activeKey = String(state.activePrintKey || "");
        const normalizedSelection = isFixedPredesignSelection(activePrintSelection)
            ? {
                ...activePrintSelection,
                attrs: applyPredesignSizeAttrs(activePrintSelection),
            }
            : activePrintSelection;
        const sizeCm = getSelectionEffectiveSizeCm(normalizedSelection);
        const techniqueLabel = resolvePrintTechniqueLabel(normalizedSelection);
        const attrs = normalizedSelection?.attrs || {};
        const inferredKind = String(attrs.print_kind || "").trim();
        const mode = String(normalizedSelection?.mode || "").toLowerCase();
        const isPredesign = inferredKind === PRINT_KIND.PREDESIGN;
        const kindLabel = inferredKind === PRINT_KIND.IMAGE_UPLOAD
            ? "Imagen propia"
            : (inferredKind === PRINT_KIND.PREDESIGN ? "Prediseño Little" : (mode === "text" ? "Texto" : "Imagen"));

        // La recomendación de técnica por tamaño (bordado↔aplique) es solo para TEXTO/letras.
        // Los prediseños/escudos derivan su técnica por categoría (no por tamaño) → no aplica.
        if (!isPredesign && techniqueLabel === "Bordado" && sizeCm > 4) {
            setPrintAdvisory({
                severity: "warning",
                ruleId: "size_bordado_gt4",
                targetTechniqueKey: "aplique",
                targetTechniqueType: MASTER_PRINT_TYPE.PATCH,
                warningScopeKey: `${activeKey}::size_bordado_gt4`,
                title: "Recomendado Aplique",
                message: `Este ${kindLabel.toLowerCase()} está en ${roundUpToHalfCm(sizeCm).toFixed(1)} cm aprox. Para tamaños mayores a 4 cm recomendamos aplicar técnica Aplique.`,
                sizeLabel: `${roundUpToHalfCm(sizeCm).toFixed(1)} cm aprox`,
                techniqueLabel,
                kindLabel,
            });
            return;
        }
        if (!isPredesign && techniqueLabel === "Aplique" && sizeCm < 5) {
            setPrintAdvisory({
                severity: "warning",
                ruleId: "size_aplique_lt5",
                targetTechniqueKey: "bordado",
                targetTechniqueType: MASTER_PRINT_TYPE.EMBROIDERY,
                warningScopeKey: `${activeKey}::size_aplique_lt5`,
                title: "Recomendado Bordado",
                message: `Este ${kindLabel.toLowerCase()} está en ${roundUpToHalfCm(sizeCm).toFixed(1)} cm aprox. Para tamaños menores a 5 cm recomendamos técnica Bordado.`,
                sizeLabel: `${roundUpToHalfCm(sizeCm).toFixed(1)} cm aprox`,
                techniqueLabel,
                kindLabel,
            });
            return;
        }
        if (isPredesign) {
            setPrintAdvisory({
                severity: "info",
                title: "Prediseño Little",
                message: techniqueLabel === "Estampado"
                    ? "Este prediseño se aplicará como Estampado."
                    : "Este prediseño se aplicará como Bordado (podés pasarlo a Estampado).",
                sizeLabel: "",
                techniqueLabel,
                kindLabel,
            });
            return;
        }
        setPrintAdvisory({
            severity: "info",
            title: "Diseño activo",
            message: `Técnica ${techniqueLabel} · tamaño ${roundUpToHalfCm(sizeCm).toFixed(1)} cm aprox.`,
            sizeLabel: `${roundUpToHalfCm(sizeCm).toFixed(1)} cm aprox`,
            techniqueLabel,
            kindLabel,
        });
    }, [activePrintSelection, state.activePrintKey]);

    useEffect(() => {
        const advisory = printAdvisory || null;
        const advisoryKey = advisory
            ? `${String(advisory.severity || "")}|${String(advisory.title || "")}|${String(advisory.message || "")}`
            : "";

        if (!advisoryKey) {
            advisoryVisibleKeyRef.current = null;
            return;
        }

        const shouldAlert = advisory?.severity === "warning" || String(advisory?.title || "") === "Prediseño Little";
        if (!shouldAlert) {
            advisoryVisibleKeyRef.current = advisoryKey;
            return;
        }

        const warningScopeKey = String(advisory?.warningScopeKey || "");
        if (advisory?.severity === "warning" && warningScopeKey) {
            advisoryWarningHitsRef.current[warningScopeKey] = Number(advisoryWarningHitsRef.current[warningScopeKey] || 0) + 1;
            if (advisoryWarningShownRef.current[warningScopeKey]) {
                return;
            }
            advisoryWarningShownRef.current[warningScopeKey] = true;
        }

        if (advisoryVisibleKeyRef.current === advisoryKey) return;
        advisoryVisibleKeyRef.current = advisoryKey;
        if (advisoryAlertKeyRef.current === advisoryKey && advisory?.severity !== "warning") return;
        advisoryAlertKeyRef.current = advisoryKey;

        const safeTitle = escapeHtml(advisory?.title || "Recomendación");
        const safeMessage = escapeHtml(advisory?.message || "");
        const safeSize = escapeHtml(advisory?.sizeLabel || "");

        Swal.fire({
            icon: "info",
            title: safeTitle,
            html: `
                <div style="text-align:left; line-height:1.45;">
                    <p style="margin:0 0 8px;">${safeMessage}</p>
                    ${safeSize ? `<p style="margin:0; color:#5a6880;">Tamaño estimado: ${safeSize}</p>` : ""}
                </div>
            `,
            showCloseButton: true,
            confirmButtonText: "Entendido",
            confirmButtonColor: "#0071ce",
        });
    }, [printAdvisory]);

    useEffect(() => {
        const advisory = printAdvisory || null;
        if (!advisory || advisory.severity !== "warning" || !activePrintSelection?.print || !state.activePrintKey) return;

        const warningScopeKey = String(advisory?.warningScopeKey || "");
        if (!warningScopeKey) return;
        if (!advisoryWarningShownRef.current[warningScopeKey]) return;
        const warningHits = Number(advisoryWarningHitsRef.current[warningScopeKey] || 0);
        if (warningHits < 2) return;
        if (advisoryWarningAutoSwitchRef.current[warningScopeKey]) return;

        const targetTechniqueKey = String(advisory?.targetTechniqueKey || "");
        const targetTechniqueType = Number(advisory?.targetTechniqueType || 0) || null;
        if (!targetTechniqueKey || !targetTechniqueType) return;

        const currentTechniqueKey = String(activePrintSelection?.attrs?.resolved_technique || "").toLowerCase();
        if (currentTechniqueKey === targetTechniqueKey) return;

        advisoryWarningAutoSwitchRef.current[warningScopeKey] = true;
        Swal.fire({
            icon: "warning",
            title: "Te ajustamos la técnica automáticamente",
            html: `
                <div style="text-align:left; line-height:1.45;">
                    <p style="margin:0 0 8px;">Ese tamaño ya no va bien con la técnica que tenías.</p>
                    <p style="margin:0;">Para que el diseño quede bien, lo pasamos automáticamente a <strong>${escapeHtml(targetTechniqueKey === "aplique" ? "Aplique" : "Bordado")}</strong>.</p>
                </div>
            `,
            showCloseButton: true,
            confirmButtonText: "Entendido",
            confirmButtonColor: "#d54ab7",
        }).then(() => {
            cmdMergePrintAttrs({
                dispatch,
                partKey: String(state.activePrintKey),
                attrs: {
                    resolved_technique: targetTechniqueKey,
                    resolved_master_print_type: String(targetTechniqueType),
                    technique_locked: "1",
                },
                context: treeContext,
            });
        });
    }, [printAdvisory, activePrintSelection, state.activePrintKey, dispatch, treeContext]);
    const getSelectionDetails = useCallback(
        (selection) => {
            if (!selection?.print) return null;
            return printDetails[selection.print.id] || selection.print;
        },
        [printDetails]
    );
    const activePrintErrors = useMemo(() => {
        if (!activePrintSelection?.print) return [];
        const details = getSelectionDetails(activePrintSelection);
        return validatePrintSelection({ selection: activePrintSelection, details });
    }, [activePrintSelection, getSelectionDetails]);

    const garmentDefaultMaterialName = useMemo(() => {
        if (!partCards.length) return "";
        const rootPart = partCards.find((part) => !part.parentLabel) || partCards[0];
        if (!rootPart?.node) return "";
        const source = Array.isArray(rootPart.node.materials) ? rootPart.node.materials : Object.values(rootPart.node.materials || {});
        const materials = filterMaterialsForPart(rootPart.label, source);
        return materials[0]?.name ? String(materials[0].name) : "";
    }, [partCards]);

    const partMaterialCards = useMemo(
        () =>
            selectPartMaterialCards({
                partCards,
                state,
                expandedPalettes,
                loadingColors,
                loadingMaterialColors,
                colorOptions,
                materialResolutionByPart,
            }),
        [partCards, state, expandedPalettes, loadingColors, loadingMaterialColors, colorOptions, materialResolutionByPart]
    );
    const filteredPartMaterialCards = partMaterialCards;
    const autoMaterialCandidates = useMemo(
        () =>
            partNodes.map((part) => {
                const key = String(part.key);
                const sourceMaterials = Array.isArray(part?.node?.materials)
                    ? part.node.materials
                    : Object.values(part?.node?.materials || {});
                return {
                    key,
                    label: part.label,
                    node: part.node,
                    selected: state.selectedKeys.has(key),
                    selectedMaterialId: state.selectedMaterials?.[key] || null,
                    materialOptions: filterMaterialsForPart(part.label, sourceMaterials),
                };
            }),
        [partNodes, state.selectedKeys, state.selectedMaterials]
    );
    const partMaterialCardByKey = useMemo(
        () => selectPartMaterialCardByKey(partMaterialCards),
        [partMaterialCards]
    );
    const partGroups = useMemo(
        () =>
            selectPartGroups({
                normalizedRoot,
                selectedKeys: state.selectedKeys,
                partMaterialCardByKey,
                activePartKey: state.activePartKey,
            }),
        [normalizedRoot, state.selectedKeys, state.activePartKey, partMaterialCardByKey]
    );

    useEffect(() => {
        if (!partGroups.length) {
            setActivePartSectionKey("");
            return;
        }
        const exists = partGroups.some((group) => String(group.key) === String(activePartSectionKey))
            || String(activePartSectionKey) === "group-stripes"
            || String(activePartSectionKey) === "group-cuts";
        if (!exists) {
            setActivePartSectionKey(String(partGroups[0].key));
        }
    }, [partGroups, activePartSectionKey]);

    const stripeWizardColors = useMemo(() => {
        const jerseyColors = colorOptions?.[String(STRIPE_JERSEY_MATERIAL_ID)] || [];
        return Array.isArray(jerseyColors) ? jerseyColors : [];
    }, [colorOptions]);

    const stripeWizard = useMemo(() => ({
        available: true,
        colors: stripeWizardColors,
        materialId: STRIPE_JERSEY_MATERIAL_ID,
    }), [stripeWizardColors]);
    const customCutWizard = useMemo(() => {
        const isCustom = designMode === "custom";
        return {
            // Recortes disponibles: siempre en «Diseño personalizado»; en modo propuesto, solo si
            // el usuario hizo opt-in ("agregar recorte extra").
            available: isCustom || customCutOptIn,
            // Se puede ofrecer el opt-in (modo propuesto y todavía sin activar).
            canOptIn: !isCustom && !customCutOptIn,
            onOptIn: () => setCustomCutOptIn(true),
            colors: Array.isArray(allColors) ? allColors : [],
        };
    }, [designMode, customCutOptIn, allColors]);

    const resolvedActivePartKey = useMemo(
        () =>
            selectResolvedActivePartKey({
                activePartKey: state.activePartKey,
                partGroups,
            }),
        [state.activePartKey, partGroups]
    );

    useEffect(() => {
        if (!isDesignerOpen) return;
        const hasEnabledSublimation = Object.values(state.partSublimation || {}).some(Boolean);
        const hasFullprints = Object.keys(state.selectedFullprints || {}).length > 0;
        const hasGeneralConfig = !!state.generalMaterialId;
        if (!hasEnabledSublimation && !hasFullprints && !state.generalSublimation && !state.generalFullprint && !hasGeneralConfig) return;

        Object.entries(state.partSublimation || {}).forEach(([partKey, enabled]) => {
            if (!enabled) return;
            dispatch({ type: DESIGNER_ACTIONS.TOGGLE_SUBLIMATION, partKey, enabled: false });
        });
        Object.keys(state.selectedFullprints || {}).forEach((partKey) => {
            dispatch({ type: DESIGNER_ACTIONS.SELECT_FULLPRINT, partKey, fullprint: null });
        });
        if (state.generalSublimation) {
            dispatch({ type: DESIGNER_ACTIONS.SET_GENERAL_SUBLIMATION, enabled: false });
        }
        if (state.generalFullprint) {
            dispatch({ type: DESIGNER_ACTIONS.SET_GENERAL_FULLPRINT, fullprint: null });
        }
        if (state.generalMaterialId) {
            dispatch({ type: DESIGNER_ACTIONS.SET_GENERAL_MATERIAL, materialId: null });
        }
    }, [
        isDesignerOpen,
        state.partSublimation,
        state.selectedFullprints,
        state.generalSublimation,
        state.generalFullprint,
        state.generalMaterialId,
    ]);

    const ensureColorsForMaterial = useCallback(async (materialId) => {
        if (!materialId) return [];
        const materialKey = String(materialId);
        const cachedColors = colorOptionsRef.current || {};
        if (Object.prototype.hasOwnProperty.call(cachedColors, materialKey)) {
            return cachedColors[materialKey] || [];
        }
        if (materialColorPromisesRef.current[materialKey]) {
            return materialColorPromisesRef.current[materialKey];
        }

        const request = (async () => {
            setLoadingMaterialColors((prev) => ({ ...prev, [materialKey]: true }));
            try {
                const response = await getAvailableColors(materialId);
                const normalizedColors = adaptAvailableColorsResponse(response);
                setColorOptions((prev) => ({ ...prev, [materialKey]: normalizedColors }));
                return normalizedColors;
            } catch (error) {
                console.warn("No se pudieron cargar colores para material", materialId, error);
                setColorOptions((prev) => ({ ...prev, [materialKey]: [] }));
                return [];
            } finally {
                setLoadingMaterialColors((prev) => ({ ...prev, [materialKey]: false }));
                delete materialColorPromisesRef.current[materialKey];
            }
        })();

        materialColorPromisesRef.current[materialKey] = request;
        return request;
    }, []);

    useEffect(() => {
        if (!isDesignerOpen) return;
        ensureColorsForMaterial(STRIPE_JERSEY_MATERIAL_ID);
    }, [isDesignerOpen, ensureColorsForMaterial]);

    useEffect(() => {
        if (!isDesignerOpen || !autoMaterialCandidates.length) return;
        let isCancelled = false;

        const resolvePartMaterials = async () => {
            const nextResolution = {};

            for (const part of autoMaterialCandidates) {
                const partKey = String(part.key);
                const materialOptions = part.materialOptions || [];
                if (!part.selected || materialOptions.length === 0) {
                    delete autoMaterialAttemptRef.current[partKey];
                    nextResolution[partKey] = { checked: false, hasMaterialWithColors: false };
                    continue;
                }

                const currentSelectedMaterial = part.selectedMaterialId || null;
                const validCurrentMaterial = currentSelectedMaterial
                    && materialOptions.some((material) => String(material.id) === String(currentSelectedMaterial))
                    ? String(currentSelectedMaterial)
                    : null;

                let resolvedMaterialWithColors = null;
                let targetMaterialId = validCurrentMaterial;

                if (!targetMaterialId) {
                    for (const material of materialOptions) {
                        const candidateMaterialId = String(material.id);
                        const candidateColors = await ensureColorsForMaterial(candidateMaterialId);
                        if (isCancelled) return;
                        if (candidateColors.length > 0) {
                            resolvedMaterialWithColors = candidateMaterialId;
                            break;
                        }
                    }
                    const fallbackMaterialId = String(materialOptions[0]?.id || "");
                    targetMaterialId = resolvedMaterialWithColors || fallbackMaterialId;
                }

                if (
                    targetMaterialId
                    && !validCurrentMaterial
                    && String(currentSelectedMaterial || "") !== String(targetMaterialId)
                    && String(autoMaterialAttemptRef.current[partKey] || "") !== String(targetMaterialId)
                ) {
                    autoMaterialAttemptRef.current[partKey] = String(targetMaterialId);
                    dispatch({
                        type: DESIGNER_ACTIONS.SELECT_MATERIAL,
                        partKey,
                        materialId: targetMaterialId,
                    });
                }

                if (validCurrentMaterial) {
                    delete autoMaterialAttemptRef.current[partKey];
                }

                nextResolution[partKey] = {
                    checked: true,
                    hasMaterialWithColors: !!resolvedMaterialWithColors || !!validCurrentMaterial,
                };
            }

            if (isCancelled) return;
            setMaterialResolutionByPart((prev) => {
                const prevKeys = Object.keys(prev);
                const nextKeys = Object.keys(nextResolution);
                if (prevKeys.length !== nextKeys.length) {
                    return nextResolution;
                }
                const same = nextKeys.every((partKey) => {
                    const prevValue = prev[partKey];
                    const nextValue = nextResolution[partKey];
                    const sameValue =
                        prevValue
                        && prevValue.checked === nextValue.checked
                        && prevValue.hasMaterialWithColors === nextValue.hasMaterialWithColors;
                    return !!sameValue;
                });
                return same ? prev : nextResolution;
            });
        };

        resolvePartMaterials();
        return () => {
            isCancelled = true;
        };
    }, [isDesignerOpen, autoMaterialCandidates, ensureColorsForMaterial]);

    useEffect(() => {
        if (!isDesignerOpen || !partMaterialCards.length) return;
        let isCancelled = false;

        const resolveDefaultWhiteColors = async () => {
            for (const part of partMaterialCards) {
                const partKey = String(part.key);
                const selectedMaterialId = part.selectedMaterialId ? String(part.selectedMaterialId) : null;
                const existingColor = selectedMaterialId
                    ? getSelectedPartColor(state, partKey, selectedMaterialId)
                    : null;

                if (
                    !part.selected
                    || !selectedMaterialId
                    || existingColor
                    || state.partSublimation?.[partKey]
                    || state.selectedFullprints?.[partKey]
                ) {
                    delete autoWhiteColorAttemptRef.current[partKey];
                    continue;
                }

                let availableColors = Array.isArray(part.availableColors) ? part.availableColors : [];
                if (!part.paletteLoaded) {
                    availableColors = await ensureColorsForMaterial(selectedMaterialId);
                    if (isCancelled) return;
                }

                const defaultPartColor = findDefaultPartColor(availableColors);
                const attemptKey = `${selectedMaterialId}:${defaultPartColor ? String(defaultPartColor.id || defaultPartColor.color_id || "default") : "none"}`;
                if (autoWhiteColorAttemptRef.current[partKey] === attemptKey) {
                    continue;
                }

                autoWhiteColorAttemptRef.current[partKey] = attemptKey;

                if (!defaultPartColor) continue;

                dispatch({
                    type: DESIGNER_ACTIONS.SELECT_COLOR,
                    partKey,
                    materialId: selectedMaterialId,
                    colorObj: normalizeColorObject(defaultPartColor),
                });
            }
        };

        resolveDefaultWhiteColors();
        return () => {
            isCancelled = true;
        };
    }, [
        isDesignerOpen,
        partMaterialCards,
        ensureColorsForMaterial,
        state,
    ]);

    const selectedPartsForPreview = useMemo(() => {
        const list = [];
        const walk = (node) => {
            if (!node) return;
            const key = String(node.key);
            if (state.selectedKeys.has(key) && node.Item) {
                const materialId = getPartMaterialId(state, node, key);
                const selectedColor = materialId ? getSelectedPartColor(state, key, materialId) : null;
                const color = selectedColor;
                list.push({
                    ...node,
                    color,
                    materialId,
                    fullprintId: null,
                    fullprintIcon: null,
                    sublimation: false,
                });
            }
            (node.children || []).forEach(walk);
        };
        walk(normalizedRoot);
        return list;
    }, [normalizedRoot, state]);

    // Al entrar al paso final, generamos la preview frente+espalda (lado a lado) para mostrarla
    // arriba del resumen. Es el mismo render del boceto, pero en layout horizontal.
    useEffect(() => {
        if (currentStepKey !== STEP.SUMMARY || !isDesignerOpen || !normalizedRoot) return;
        let cancelled = false;
        setFinalPreviewLoading(true);
        (async () => {
            try {
                const img = await renderSketchImage({
                    selectedParts: selectedPartsForPreview,
                    root: normalizedRoot,
                    selectedPrints: state.selectedPrints,
                    allColors,
                    stripeDrawingsBySide,
                    customCutsBySide,
                    customCutZoneColors,
                    customCutZonesBySide,
                    customCutRuntimeBySide: customCutRuntimeBySideRef?.current || null,
                    layout: "horizontal",
                    decorations: false, // resumen del cliente: fondo limpio, sin marca de agua
                });
                if (!cancelled) setFinalPreviewImage(img || null);
            } catch {
                if (!cancelled) setFinalPreviewImage(null);
            } finally {
                if (!cancelled) setFinalPreviewLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStepKey, isDesignerOpen, normalizedRoot, selectedPartsForPreview, state.selectedPrints, stripeDrawingsBySide, customCutsBySide]);

    const hasBackView = useMemo(
        () =>
            selectedPartsForPreview.some((part) => {
                const item = part?.Item || {};
                return !!item.img_back || !!item.img_back_t;
            }),
        [selectedPartsForPreview]
    );

    useEffect(() => {
        if (hasBackView) return;
        if (state.viewSide !== "front") {
            dispatch({ type: DESIGNER_ACTIONS.SET_VIEW_SIDE, viewSide: "front" });
        }
    }, [hasBackView, state.viewSide]);

    const appliedPartsPreview = useMemo(
        () =>
            buildAppliedPartsPreview({
                root: normalizedRoot,
                selectedKeys: state.selectedKeys,
                selectedPrints: state.selectedPrints,
                selectedColors: state.selectedColors,
                selectedMaterials: state.selectedMaterials,
                partAttrsByKey: {},
                generalMaterialId: null,
                selectedFullprints: {},
                partSublimation: {},
            }),
        [normalizedRoot, state]
    );

    const appliedPartsBySection = useMemo(() => {
        const grouped = new Map();
        (partMaterialCards || []).forEach((part) => {
            if (!part?.selected) return;
            if (shouldHideDesignerPart({
                groupLabel: part.parentLabel,
                partLabel: part.label,
                node: part.node,
            })) return;
            const colorName = part.selectedColor?.name || null;
            const colorCode = part.selectedColor?.code || null;
            if (!colorName && !colorCode) return;
            const rawSection = String(part.parentLabel || "").trim();
            const section = rawSection && rawSection.toLowerCase() !== "master" ? rawSection : "Base";
            if (!grouped.has(section)) grouped.set(section, []);
            grouped.get(section).push({
                key: String(part.key),
                label: part.label || `Parte ${part.key}`,
                colorName,
                colorCode,
                region: String(part.node?.region ?? part.node?.Item?.region ?? "").toLowerCase() || null,
            });
        });

        return Array.from(grouped.entries()).map(([section, items]) => ({
            section,
            items: items.sort((a, b) => String(a.label).localeCompare(String(b.label))),
        }));
    }, [partMaterialCards]);

    const stripeStrokeSummary = useMemo(() => {
        const front = Array.isArray(stripeDrawingsBySide?.front) ? stripeDrawingsBySide.front.length : 0;
        const back = Array.isArray(stripeDrawingsBySide?.back) ? stripeDrawingsBySide.back.length : 0;
        const uniqueTotal = countUniqueStripeStrokes(stripeDrawingsBySide);
        const byTool = summarizeStripeStrokesByTool(stripeDrawingsBySide);
        // Colores usados en las tiras (para el resumen del cliente): {code, count} únicos por color.
        const colorMap = new Map();
        ["front", "back"].forEach((side) => {
            (Array.isArray(stripeDrawingsBySide?.[side]) ? stripeDrawingsBySide[side] : []).forEach((stroke) => {
                const code = String(stroke?.color || "").trim().toLowerCase();
                if (!code) return;
                colorMap.set(code, (colorMap.get(code) || 0) + 1);
            });
        });
        return {
            front,
            back,
            total: uniqueTotal > 0 ? uniqueTotal : Math.max(front, back),
            straight: byTool.straight,
            curved: byTool.curved,
            colors: Array.from(colorMap.entries()).map(([code, count]) => ({ code, count })),
        };
    }, [stripeDrawingsBySide]);
    const customCutSummary = useMemo(() => ({
        total: countUniqueCustomCuts(customCutsBySide),
        front: Array.isArray(customCutsBySide?.front) ? customCutsBySide.front.length : 0,
        back: Array.isArray(customCutsBySide?.back) ? customCutsBySide.back.length : 0,
        coloredZones: Object.keys(customCutZoneColors || {}).length,
        details: buildCustomCutColorDetails(customCutsBySide, customCutZoneColors, customCutZonesBySide),
    }), [customCutsBySide, customCutZoneColors, customCutZonesBySide]);

    const appliedPrintsBySection = useMemo(() => {
        const grouped = new Map();
        Object.entries(state.selectedPrints || {}).forEach(([printKey, selection]) => {
            if (!selection?.print) return;
            if (!hasRealPrintContent(selection)) return;
            const locationKey = String(getPrintLocationKeyFromSelection(printKey, selection));
            if (!locationKey) return;
            const locationNode = findNode(normalizedRoot, locationKey);
            const sectionRaw = String(printLocationMetaByKey[locationKey]?.partLabel || "").trim();
            const section = sectionRaw && sectionRaw.toLowerCase() !== "master" ? sectionRaw : "Base";
            const locationLabel = sanitizePrintLocationLabel(
                locationNode?.Item?.nname
                || locationNode?.Item?.name
                || locationNode?.master_parts?.nice_name
                || locationNode?.master_part?.nice_name
                || "Diseño"
            );
            if (!grouped.has(section)) grouped.set(section, []);
            grouped.get(section).push({
                key: String(printKey),
                locationKey,
                locationLabel,
                techniqueLabel: resolvePrintTechniqueLabel(selection),
                previewUrl: resolvePrintPreviewUrl(selection),
                mode: selection?.mode || null,
                isText: selection?.mode === "text" || !!selection?.attrs?.text,
                textValue: String(selection?.attrs?.text || "").trim(),
                isPersonal: String(selection?.attrs?.personal || "") === "1",
            });
        });

        const sections = Array.from(grouped.entries()).map(([section, items]) => ({
            section,
            items: items.sort((a, b) => String(a.locationLabel).localeCompare(String(b.locationLabel))),
        }));
        if (stripeStrokeSummary.total > 0) {
            sections.push({
                section: "Tiras",
                items: [{
                    key: "stripe-summary",
                    locationKey: "stripes",
                    locationLabel: "Tiras aplicadas",
                    techniqueLabel: `${stripeStrokeSummary.total} trazo${stripeStrokeSummary.total === 1 ? "" : "s"} · Rectas ${stripeStrokeSummary.straight} · Serpentinas ${stripeStrokeSummary.curved}`,
                    previewUrl: null,
                    mode: "stripes",
                    isText: false,
                    isStripeSummary: true,
                    strokeCount: stripeStrokeSummary.total,
                    straightCount: stripeStrokeSummary.straight,
                    serpentinaCount: stripeStrokeSummary.curved,
                }],
            });
        }
        return sections;
    }, [state.selectedPrints, normalizedRoot, printLocationMetaByKey, stripeStrokeSummary]);

    const resultTree = useMemo(
        () => (normalizedRoot ? buildFullSelectionTree(normalizedRoot, state.selectedKeys) : null),
        [normalizedRoot, state.selectedKeys]
    );

    const resultTreeArray = useMemo(() => {
        if (!resultTree) return [];
        if (String(resultTree.key) === "master") return [{ ...resultTree, selected: true }];
        return [{ key: "master", selected: true, children: [resultTree] }];
    }, [resultTree]);

    const blockingIssues = useMemo(
        () =>
            collectBlockingIssues({
                root: normalizedRoot,
                context: treeContext,
                state,
                allColors,
                printDetails,
            }),
        [normalizedRoot, treeContext, state, allColors, printDetails]
    );

    const stepBlockingBySection = useMemo(() => {
        // Grupos de variantes requeridas sin elegir, filtrables por tipo de parte.
        const missingExclusiveRequiredFor = (typeMatches) =>
            Object.values(treeContext.exclusiveGroups || {}).filter((group) => {
                if (!group || group.requiredKeys?.size === 0) return false;
                if (!typeMatches(Number(group.type))) return false;
                const parentKey = String(group.parentKey || "");
                if (parentKey && parentKey !== "master" && !state.selectedKeys.has(parentKey)) return false;
                return !group.keys.some((key) => state.selectedKeys.has(String(key)));
            });
        const isPartTypeVariant = (type) => type === Number(MASTER_PART_TYPE.PART_TYPE);

        if (currentStepKey === STEP.MODEL) {
            return [{ title: "Modelo", items: [] }];
        }
        if (currentStepKey === STEP.DETAILS) {
            const items = [];
            missingExclusiveRequiredFor(isPartTypeVariant).forEach((group) => {
                const parentNode = treeContext.nodeByKey[String(group.parentKey)];
                const parentLabel = parentNode?.Item?.nname || parentNode?.Item?.name || "Detalle";
                items.push(`${parentLabel}: elegí una variante.`);
            });
            return [{ title: "Cambiar detalles", items }];
        }
        if (currentStepKey === STEP.PAINT) {
            const sectionItemsMap = new Map();
            const addSectionItem = (sectionTitle, itemMessage) => {
                const sectionKey = String(sectionTitle || "General").trim() || "General";
                const item = String(itemMessage || "").trim();
                if (!item) return;
                if (!sectionItemsMap.has(sectionKey)) {
                    sectionItemsMap.set(sectionKey, new Set());
                }
                sectionItemsMap.get(sectionKey).add(item);
            };

            // Variantes izquierda/derecha (y las tipo-7 si el modelo no tiene el paso "Cambiar detalles").
            const missingExclusiveRequired = missingExclusiveRequiredFor(
                (type) => !isPartTypeVariant(type) || !modelHasDetailVariants
            );
            missingExclusiveRequired.forEach((group) => {
                const parentNode = treeContext.nodeByKey[String(group.parentKey)];
                const parentLabel = parentNode?.Item?.nname || parentNode?.Item?.name || "Parte requerida";
                addSectionItem(parentLabel, "Falta seleccionar una variante requerida.");
            });

            const missingRequired = partCards.filter((part) => part.required && !part.selected);
            missingRequired.forEach((part) => {
                addSectionItem(part.parentLabel || part.label || "General", `${part.label}: falta seleccionar parte requerida.`);
            });

            const incompleteSelected = partCards.filter((part) => part.selected && !part.complete);
            incompleteSelected.forEach((part) => {
                addSectionItem(part.parentLabel || part.label || "General", `${part.label}: falta ${part.missing.join(", ")}.`);
            });

            return Array.from(sectionItemsMap.entries()).map(([title, set]) => ({
                title,
                items: Array.from(set),
            }));
        }
        if (currentStepKey === STEP.PRINTS) {
            const messages = [];
            const allSelections = Object.values(state.selectedPrints || {});
            allSelections.forEach((selection) => {
                if (!selection?.print) return;
                const details = printDetails[selection.print.id] || selection.print;
                const errors = validatePrintSelection({ selection, details });
                errors.forEach((error) => messages.push(error));
            });
            activePrintErrors.forEach((error) => messages.push(error));
            return [{
                title: "Diseños",
                items: Array.from(new Set(messages)),
            }];
        }
        if (currentStepKey === STEP.SUMMARY) {
            return [{
                title: "Resumen",
                items: blockingIssues.map((issue) => issue.message),
            }];
        }
        // Tiras aplicadas (paso opcional) y cualquier otro: sin bloqueo.
        return [{ title: STEP_TITLES[currentStepKey] || "General", items: [] }];
    }, [
        currentStepKey,
        modelHasDetailVariants,
        partCards,
        state,
        treeContext,
        activePrintErrors,
        printDetails,
        blockingIssues,
    ]);

    const stepBlockingMessages = useMemo(
        () =>
            (stepBlockingBySection || [])
                .flatMap((section) => section.items || [])
                .filter(Boolean),
        [stepBlockingBySection]
    );

    const stepBlockReason = useMemo(() => {
        if (stepBlockingMessages.length > 0) {
            return stepBlockingMessages[0];
        }
        return null;
    }, [stepBlockingMessages]);
    const saveBlockedByExistingDatasheet = !datasheetGuard.loading && datasheetGuard.canCreate === false;
    const saveDisabled = blockingIssues.length > 0 || saveBlockedByExistingDatasheet;
    const saveMsgLooksSuccess = typeof saveMsg === "string" && (
        saveMsg.toLowerCase().includes("guardado correcto")
        || saveMsg.toLowerCase().includes("éxito")
    );

    const normalizePrintSideKey = useCallback((value) => (Number(value) === 1 || String(value).toLowerCase() === "back" ? "back" : "front"), []);
    const resolveAvailablePrintHotspot = useCallback((requestedLocationKey, selectedSide, excludePrintKey = null) => {
        const normalizedSide = normalizePrintSideKey(selectedSide);
        const candidates = printHotspotMetrics.list.filter((spot) => String(spot?.side || "") === normalizedSide);
        if (!candidates.length) return null;

        const isAvailable = (spot) => {
            const occupancyKey = buildPrintOccupancyKey(spot?.key, normalizedSide);
            const occupants = occupiedPrintHotspots.get(occupancyKey);
            if (!occupants || occupants.size === 0) return true;
            if (!excludePrintKey) return false;
            return Array.from(occupants).every((occupantKey) => String(occupantKey) === String(excludePrintKey));
        };

        const requestedSpot = printHotspotMetrics.map[String(requestedLocationKey || "")] || null;
        if (requestedSpot && String(requestedSpot?.side || "") === normalizedSide && isAvailable(requestedSpot)) {
            return requestedSpot;
        }

        const freeCandidates = candidates.filter((spot) => isAvailable(spot));
        if (!freeCandidates.length) return null;
        return freeCandidates
            .slice()
            .sort((a, b) => {
                const distA = ((Number(a?.leftPct || 0) - PRINT_SPAWN_CENTER.leftPct) ** 2)
                    + ((Number(a?.topPct || 0) - PRINT_SPAWN_CENTER.topPct) ** 2);
                const distB = ((Number(b?.leftPct || 0) - PRINT_SPAWN_CENTER.leftPct) ** 2)
                    + ((Number(b?.topPct || 0) - PRINT_SPAWN_CENTER.topPct) ** 2);
                return distA - distB;
            })[0];
    }, [normalizePrintSideKey, occupiedPrintHotspots, printHotspotMetrics]);

    const applySelectPrint = useCallback(async (printKey, locationKey, print, sideFromUser, initialAttrs = null) => {
        const normalizedSide = normalizePrintSideKey(sideFromUser);
        const targetSpot = resolveAvailablePrintHotspot(locationKey, normalizedSide, printKey);
        if (!targetSpot) {
            await Swal.fire({
                icon: "warning",
                title: "Sin ubicación disponible",
                text: `No quedan ubicaciones libres en ${normalizedSide === "back" ? "Espalda" : "Frente"} para agregar otro diseño.`,
                showCloseButton: true,
                confirmButtonText: "Entendido",
                confirmButtonColor: "#d54ab7",
            });
            return null;
        }
        const normalizedInitialAttrs = { ...(initialAttrs || {}) };
        const fixedPredesign = isFixedPredesignSelection({
            print,
            mode: normalizedInitialAttrs?.print_kind === PRINT_KIND.PREDESIGN ? "predesign" : (normalizedInitialAttrs?.print_kind === PRINT_KIND.TEXT ? "text" : "image"),
            attrs: normalizedInitialAttrs,
        });
        const placementAttrs = {
            ...normalizedInitialAttrs,
            offset_x_pct: String((PRINT_SPAWN_CENTER.leftPct - Number(targetSpot.leftPct || 0)).toFixed(2)),
            offset_y_pct: String((PRINT_SPAWN_CENTER.topPct - Number(targetSpot.topPct || 0)).toFixed(2)),
            offset_origin: "hotspot",
            scale_pct: String(normalizedInitialAttrs.scale_pct || "100"),
        };
        const normalizedPlacementAttrs = fixedPredesign
            ? applyPredesignSizeAttrs(placementAttrs, normalizedInitialAttrs?.predesign_size_tier, print)
            : placementAttrs;
        cmdSelectPrint({
            dispatch,
            treeContext,
            preloadPrintDetailsByIds,
            printKey,
            locationKey: String(targetSpot.key),
            print,
            side: sideFromUser,
            initialAttrs: normalizedPlacementAttrs,
        });
        return {
            printKey: String(printKey),
            locationKey: String(targetSpot.key),
        };
    }, [dispatch, normalizePrintSideKey, preloadPrintDetailsByIds, resolveAvailablePrintHotspot, treeContext]);

    const askPrintSide = useCallback(async () => {
        const result = await Swal.fire({
            icon: "question",
            title: "¿Dónde querés pegar el diseño?",
            text: "Podés elegir Frente o Espalda antes de editarlo.",
            showCancelButton: true,
            showCloseButton: true,
            showDenyButton: true,
            confirmButtonText: "Frente",
            denyButtonText: "Espalda",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#0071ce",
            denyButtonColor: "#264653",
            cancelButtonColor: "#d54ab7",
        });
        if (result.isDismissed) return null;
        if (result.isDenied) return 1;
        return 0;
    }, []);

    const handleSelectPrint = useCallback(async (locationKey, print, options = {}) => {
        const normalizedLocationKey = String(locationKey);
        const activeKey = String(state.activePrintKey || "");
        const activeIsSameLocation = activeKey
            && getPrintLocationKeyFromSelection(activeKey, state.selectedPrints[activeKey]) === normalizedLocationKey;
        const targetPrintKey = options.append
            ? buildPrintInstanceKey(normalizedLocationKey)
            : (options.printKey || (activeIsSameLocation ? activeKey : buildPrintInstanceKey(normalizedLocationKey)));
        const selectedSide = await askPrintSide();
        if (selectedSide === null) return null;
        cmdSetViewSide({
            dispatch,
            viewSide: selectedSide === 1 ? "back" : "front",
        });
        const inserted = await applySelectPrint(String(targetPrintKey), normalizedLocationKey, print, selectedSide, options.initialAttrs || null);
        return inserted?.printKey || null;
    }, [state.activePrintKey, state.selectedPrints, askPrintSide, applySelectPrint]);

    const askPrintColors = useCallback(({ title, colorSlots, defaultSelectedColors = [] }) => (
        new Promise((resolve) => {
            if (printColorQuickSetupResolverRef.current) {
                printColorQuickSetupResolverRef.current(null);
            }
            printColorQuickSetupResolverRef.current = resolve;
            setPrintColorQuickSetup({
                title,
                colorSlots,
                initialColorIds: defaultSelectedColors,
            });
        })
    ), []);

    const handleAddPrint = useCallback(async (locationKey) => {
        const printTypeResult = await Swal.fire({
            icon: "question",
            title: "Tipo de diseño",
            input: "select",
            inputOptions: {
                [PRINT_KIND.TEXT]: "Texto",
                [PRINT_KIND.PREDESIGN]: "Prediseño Little",
                [PRINT_KIND.IMAGE_UPLOAD]: "Imagen propia",
            },
            inputValue: PRINT_KIND.TEXT,
            inputPlaceholder: "Elegí un tipo",
            showCancelButton: true,
            showCloseButton: true,
            confirmButtonText: "Continuar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#0071ce",
            cancelButtonColor: "#d54ab7",
        });
        if (printTypeResult.isDismissed) return null;
        const selectedPrintKind = String(printTypeResult.value || PRINT_KIND.TEXT);
        const mode = selectedPrintKind === PRINT_KIND.TEXT ? "text" : "image";

        let selectedTechniqueId = MASTER_PRINT_TYPE.EMBROIDERY;
        let selectedTechnique = TECHNIQUE_OPTIONS[0];
        let usePredesignEstampado = false;
        if (selectedPrintKind === PRINT_KIND.TEXT) {
            const techniqueResult = await Swal.fire({
                icon: "question",
                title: "Técnica",
                input: "select",
                inputOptions: TECHNIQUE_OPTIONS.reduce((acc, option) => {
                    acc[String(option.id)] = option.label;
                    return acc;
                }, {}),
                inputValue: String(MASTER_PRINT_TYPE.EMBROIDERY),
                inputPlaceholder: "Elegí técnica",
                showCancelButton: true,
                showCloseButton: true,
                confirmButtonText: "Continuar",
                cancelButtonText: "Cancelar",
                confirmButtonColor: "#0071ce",
                cancelButtonColor: "#d54ab7",
            });
            if (techniqueResult.isDismissed) return null;
            selectedTechniqueId = Number(techniqueResult.value || MASTER_PRINT_TYPE.EMBROIDERY);
            selectedTechnique = TECHNIQUE_OPTIONS.find((item) => item.id === selectedTechniqueId) || TECHNIQUE_OPTIONS[0];
        } else if (selectedPrintKind === PRINT_KIND.PREDESIGN) {
            const estampadoToggle = await Swal.fire({
                icon: "question",
                title: "Técnica del prediseño",
                html: `
                    <label style="display:flex; align-items:center; gap:10px; justify-content:center; margin-top:6px;">
                        <input id="swal-predesign-use-estampado" type="checkbox" checked />
                        <span>Usar estampado</span>
                    </label>
                    <p style="margin-top:10px; color:#5a6880;">Ya lo dejamos listo como Estampado para que sigas de una.</p>
                `,
                showCancelButton: true,
                showCloseButton: true,
                confirmButtonText: "Continuar",
                cancelButtonText: "Cancelar",
                confirmButtonColor: "#0071ce",
                cancelButtonColor: "#d54ab7",
                preConfirm: () => {
                    const node = document.getElementById("swal-predesign-use-estampado");
                    return node?.checked ? "1" : "1";
                },
            });
            if (estampadoToggle.isDismissed) return null;
            usePredesignEstampado = String(estampadoToggle.value || "0") === "1";
            selectedTechniqueId = usePredesignEstampado ? MASTER_PRINT_TYPE.SERIGRAPHY : MASTER_PRINT_TYPE.EMBROIDERY;
            selectedTechnique = TECHNIQUE_OPTIONS.find((item) => item.id === selectedTechniqueId) || TECHNIQUE_OPTIONS[0];
        } else {
            selectedTechniqueId = MASTER_PRINT_TYPE.SERIGRAPHY;
            selectedTechnique = TECHNIQUE_OPTIONS.find((item) => item.id === selectedTechniqueId) || TECHNIQUE_OPTIONS[2];
        }

        let colorIdsBySlot = [];
        let isPersonalText = false;
        const lastTextSelection = Object.values(state.selectedPrints || {})
            .reverse()
            .find((selection) => {
                const mode = String(selection?.mode || "").toLowerCase();
                const text = String(selection?.attrs?.text || "").trim();
                return mode === "text" || text.length > 0;
            }) || null;
        const inheritedTextFontId = lastTextSelection?.attrs?.font ? String(lastTextSelection.attrs.font) : "";
        const inheritedPersonalText = String(lastTextSelection?.attrs?.personal || "0") === "1";
        const inheritedTextColors = [
            String(lastTextSelection?.attrs?.color1 || lastTextSelection?.attrs?.color || "").trim(),
            String(lastTextSelection?.attrs?.color2 || "").trim(),
            String(lastTextSelection?.attrs?.color3 || "").trim(),
        ].filter(Boolean);
        if (selectedPrintKind === PRINT_KIND.TEXT) {
            const personalToggle = await Swal.fire({
                icon: "question",
                title: "Tipo de texto",
                html: `
                    <label style="display:inline-flex; align-items:center; gap:10px; justify-content:center; margin:6px auto 0; width:fit-content;">
                        <input id="swal-text-use-personal" type="checkbox" ${inheritedPersonalText ? "checked" : ""} style="position:static; margin:0; width:20px; height:20px; accent-color:#d54ab7; transform:none;" />
                        <span>Texto personal (cada uno con su apodo)</span>
                    </label>
                    <p style="margin-top:10px; color:#5a6880;">Si lo prendés, cada uno puede tener su apodo.</p>
                `,
                showCancelButton: true,
                showCloseButton: true,
                confirmButtonText: "Continuar",
                cancelButtonText: "Cancelar",
                confirmButtonColor: "#0071ce",
                cancelButtonColor: "#d54ab7",
                preConfirm: () => {
                    const node = document.getElementById("swal-text-use-personal");
                    return node?.checked ? "1" : "0";
                },
            });
            if (personalToggle.isDismissed) return null;
            isPersonalText = String(personalToggle.value || "0") === "1";

            const colorSlots = selectedTechnique.key === "bordado"
                ? 1
                : Math.max(1, Number(getPrintColorSlots(selectedTechniqueId) || 1));
            if (inheritedTextColors.length > 0) {
                colorIdsBySlot = inheritedTextColors.slice(0, colorSlots);
            } else {
                const selectedColors = await askPrintColors({
                    title: `Colores · ${selectedTechnique.label}`,
                    colorSlots,
                    defaultSelectedColors: inheritedTextColors.slice(0, colorSlots),
                });
                if (!selectedColors) return null;
                colorIdsBySlot = selectedColors;
            }
        }

        const fallbackLocationKey = locationKey || allPrintLocations?.[0]?.key || null;
        if (!fallbackLocationKey) return null;
        const normalizedLocationKey = String(fallbackLocationKey);
        if (selectedPrintKind === PRINT_KIND.PREDESIGN) {
            return {
                mode: "predesign",
                locationKey: normalizedLocationKey,
                options: {
                    useEstampado: usePredesignEstampado,
                    techniqueId: selectedTechniqueId,
                    techniqueKey: selectedTechnique.key,
                },
            };
        }

        const locationNode =
            treeContext?.nodeByKey?.[normalizedLocationKey]
            || allPrintLocations.find((location) => String(location?.key) === normalizedLocationKey)?.node
            || null;
        const preferredCandidates = filterPrintsForLocation({
            locationNode,
            filters: {
                mode,
                technique: String(selectedTechniqueId),
                ...(selectedPrintKind === PRINT_KIND.TEXT
                    ? { scope: isPersonalText ? "personal" : "group" }
                    : {}),
            },
        });
        const techniqueCandidates = filterPrintsForLocation({
            locationNode,
            filters: {
                technique: String(selectedTechniqueId),
                ...(selectedPrintKind === PRINT_KIND.TEXT
                    ? { scope: isPersonalText ? "personal" : "group" }
                    : {}),
            },
        });
        const modeCandidates = filterPrintsForLocation({
            locationNode,
            filters: {
                mode,
            },
        });
        const allCandidates = Array.isArray(locationNode?.prints)
            ? locationNode.prints
            : Object.values(locationNode?.prints || {});
        const basePrint =
            resolveBestBasePrintCandidate({
                candidates: preferredCandidates,
                mode,
                isPersonalText,
            })
            || resolveBestBasePrintCandidate({
                candidates: techniqueCandidates,
                mode,
                isPersonalText,
            })
            || resolveBestBasePrintCandidate({
                candidates: modeCandidates,
                mode,
                isPersonalText,
            })
            || resolveBestBasePrintCandidate({
                candidates: allCandidates,
                mode,
                isPersonalText,
            })
            || null;
        dispatch({
            type: DESIGNER_ACTIONS.SET_PRINT_FILTER,
            partKey: normalizedLocationKey,
            filterName: "mode",
            value: mode,
        });
        const initialAttrs = {
            resolved_master_print_type: String(selectedTechniqueId),
            resolved_technique: String(selectedTechnique.key),
            technique_locked: "1",
            print_kind: selectedPrintKind,
        };
        if (selectedPrintKind === PRINT_KIND.TEXT) {
            initialAttrs.freeform_custom_print = "1";
            initialAttrs.text = "Apodo";
            initialAttrs.personal = isPersonalText ? "1" : "0";
            if (inheritedTextFontId) {
                initialAttrs.font = inheritedTextFontId;
            }
            const normalizedBySlot = Array.isArray(colorIdsBySlot)
                ? colorIdsBySlot.map((value) => String(value || "").trim())
                : [];
            const hasAnyColor = normalizedBySlot.some(Boolean);
            if (!hasAnyColor) {
                const defaultWhite = (allColors || []).find((color) => {
                    const code = String(normalizeColorCode(color?.code) || "").toLowerCase();
                    const name = String(color?.name || "").toLowerCase();
                    return code === "#ffffff" || code === "#fff" || name.includes("blanco") || name.includes("white");
                });
                if (defaultWhite?.id) {
                    normalizedBySlot[0] = String(defaultWhite.id);
                }
            }
            normalizedBySlot.forEach((colorId, index) => {
                if (!colorId) return;
                const slot = index + 1;
                initialAttrs[`color${slot}`] = colorId;
                if (slot === 1) initialAttrs.color = colorId;
            });
        } else {
            initialAttrs.print_kind = PRINT_KIND.IMAGE_UPLOAD;
            initialAttrs.resolved_master_print_type = String(MASTER_PRINT_TYPE.SERIGRAPHY);
            initialAttrs.resolved_technique = "estampado";
            initialAttrs.freeform_custom_print = "1";
        }
        const insertedPrintKey = await handleSelectPrint(
            normalizedLocationKey,
            buildSyntheticPrintForMode(normalizedLocationKey, mode, basePrint),
            { append: true, initialAttrs }
        );
        return insertedPrintKey ? { printKey: insertedPrintKey, mode } : null;
    }, [askPrintColors, handleSelectPrint, treeContext, allPrintLocations, allColors, state.selectedPrints]);

    // Versión PROGRAMÁTICA de "agregar texto" (sin los popups de Swal) para que la dispare el
    // wizard nuevo de tarjetas. Replica el tramo de inserción de handleAddPrint (modo texto).
    const handleAddTextPrintDirect = useCallback(async ({ locationKey = null, view = "front", technique = "bordado", isPersonalText = false, text = "Apodo", colorIds = [] } = {}) => {
        const selectedTechnique = TECHNIQUE_OPTIONS.find((t) => t.key === String(technique)) || TECHNIQUE_OPTIONS[0];
        const selectedTechniqueId = selectedTechnique.id;
        const mode = "text";
        const fallbackLocationKey = locationKey || allPrintLocations?.[0]?.key || null;
        if (!fallbackLocationKey) return null;
        const normalizedLocationKey = String(fallbackLocationKey);
        const locationNode =
            treeContext?.nodeByKey?.[normalizedLocationKey]
            || allPrintLocations.find((location) => String(location?.key) === normalizedLocationKey)?.node
            || null;
        const scopeFilter = isPersonalText ? "personal" : "group";
        const basePrint =
            resolveBestBasePrintCandidate({ candidates: filterPrintsForLocation({ locationNode, filters: { mode, technique: String(selectedTechniqueId), scope: scopeFilter } }), mode, isPersonalText })
            || resolveBestBasePrintCandidate({ candidates: filterPrintsForLocation({ locationNode, filters: { technique: String(selectedTechniqueId), scope: scopeFilter } }), mode, isPersonalText })
            || resolveBestBasePrintCandidate({ candidates: filterPrintsForLocation({ locationNode, filters: { mode } }), mode, isPersonalText })
            || resolveBestBasePrintCandidate({ candidates: (Array.isArray(locationNode?.prints) ? locationNode.prints : Object.values(locationNode?.prints || {})), mode, isPersonalText })
            || null;
        dispatch({ type: DESIGNER_ACTIONS.SET_PRINT_FILTER, partKey: normalizedLocationKey, filterName: "mode", value: mode });
        const initialAttrs = {
            resolved_master_print_type: String(selectedTechniqueId),
            resolved_technique: String(selectedTechnique.key),
            technique_locked: "1",
            print_kind: PRINT_KIND.TEXT,
            freeform_custom_print: "1",
            text: text != null && String(text).length ? String(text) : "Apodo",
            personal: isPersonalText ? "1" : "0",
        };
        const normalizedBySlot = Array.isArray(colorIds) ? colorIds.map((value) => String(value || "").trim()) : [];
        if (!normalizedBySlot.some(Boolean)) {
            const defaultWhite = (allColors || []).find((color) => {
                const code = String(normalizeColorCode(color?.code) || "").toLowerCase();
                const name = String(color?.name || "").toLowerCase();
                return code === "#ffffff" || code === "#fff" || name.includes("blanco") || name.includes("white");
            });
            if (defaultWhite?.id) normalizedBySlot[0] = String(defaultWhite.id);
        }
        normalizedBySlot.forEach((colorId, index) => {
            if (!colorId) return;
            const slot = index + 1;
            initialAttrs[`color${slot}`] = colorId;
            if (slot === 1) initialAttrs.color = colorId;
        });
        // Lado según la vista elegida (frente=0, espalda=1; mangas → frente). Llamamos a
        // applySelectPrint DIRECTO (no handleSelectPrint) para NO abrir el popup de lado y
        // caer en el lado correcto.
        const side = String(view) === "back" ? 1 : 0;
        cmdSetViewSide({ dispatch, viewSide: side === 1 ? "back" : "front" });
        const targetPrintKey = buildPrintInstanceKey(normalizedLocationKey);
        const inserted = await applySelectPrint(
            String(targetPrintKey),
            normalizedLocationKey,
            buildSyntheticPrintForMode(normalizedLocationKey, mode, basePrint),
            side,
            initialAttrs
        );
        return inserted?.printKey ? { printKey: inserted.printKey, mode } : null;
    }, [applySelectPrint, treeContext, allPrintLocations, allColors]);

    // Pega una IMAGEN PROPIA (subida por el cliente) como print en la prenda (estampado a todo color).
    const handleAddImagePrint = useCallback(async ({ locationKey = null, view = "front", imageUrl = "", imageMode = "contain" } = {}) => {
        if (!imageUrl) return null;
        const mode = "image";
        const fallbackLocationKey = locationKey || allPrintLocations?.[0]?.key || null;
        if (!fallbackLocationKey) return null;
        const normalizedLocationKey = String(fallbackLocationKey);
        const locationNode =
            treeContext?.nodeByKey?.[normalizedLocationKey]
            || allPrintLocations.find((location) => String(location?.key) === normalizedLocationKey)?.node
            || null;
        const basePrint =
            resolveBestBasePrintCandidate({ candidates: filterPrintsForLocation({ locationNode, filters: { mode } }), mode })
            || resolveBestBasePrintCandidate({ candidates: (Array.isArray(locationNode?.prints) ? locationNode.prints : Object.values(locationNode?.prints || {})), mode })
            || null;
        dispatch({ type: DESIGNER_ACTIONS.SET_PRINT_FILTER, partKey: normalizedLocationKey, filterName: "mode", value: mode });
        const initialAttrs = {
            resolved_master_print_type: String(MASTER_PRINT_TYPE.SERIGRAPHY),
            resolved_technique: "estampado",
            technique_locked: "1",
            print_kind: PRINT_KIND.IMAGE_UPLOAD,
            freeform_custom_print: "1",
            image_url: imageUrl,
            image_mode: imageMode || "contain",
        };
        const side = String(view) === "back" ? 1 : 0;
        cmdSetViewSide({ dispatch, viewSide: side === 1 ? "back" : "front" });
        const targetPrintKey = buildPrintInstanceKey(normalizedLocationKey);
        const inserted = await applySelectPrint(
            String(targetPrintKey),
            normalizedLocationKey,
            buildSyntheticPrintForMode(normalizedLocationKey, mode, basePrint),
            side,
            initialAttrs
        );
        return inserted?.printKey ? { printKey: inserted.printKey, mode } : null;
    }, [applySelectPrint, treeContext, allPrintLocations]);

    const handlePrepareLittleDesign = useCallback(async (locationKey, predesign, options = null) => {
        if (!predesign) return;
        const useEstampado = Boolean(options?.useEstampado);
        const selectedTechniqueId = Number(
            options?.techniqueId
            || (useEstampado ? MASTER_PRINT_TYPE.SERIGRAPHY : MASTER_PRINT_TYPE.EMBROIDERY)
        );
        const selectedTechnique = TECHNIQUE_OPTIONS.find((item) => item.id === selectedTechniqueId) || TECHNIQUE_OPTIONS[0];
        // El wizard nuevo ya eligió la vista → puede pasar el lado y saltear el popup.
        const selectedSide = (options?.side === 0 || options?.side === 1) ? options.side : await askPrintSide();
        if (selectedSide === null) return null;

        const fallbackLocationKey =
            locationKey
            || allPrintLocations?.[0]?.key
            || null;
        if (!fallbackLocationKey) return null;
        const normalizedLocationKey = String(fallbackLocationKey);
        const initialAttrs = {
            resolved_master_print_type: String(selectedTechniqueId),
            resolved_technique: String(selectedTechnique.key),
            technique_locked: "1",
            print_kind: PRINT_KIND.PREDESIGN,
            predesign_use_estampado: useEstampado ? "1" : "0",
        };
        return {
            locationKey: normalizedLocationKey,
            predesign,
            side: selectedSide,
            initialAttrs,
        };
    }, [allPrintLocations, askPrintSide]);

    const handleApplyLittleDesign = useCallback(async (preparedSelection, colorIds = [], sizeTier = null) => {
        if (!preparedSelection?.predesign || !preparedSelection?.locationKey) return null;
        const normalizedLocationKey = String(preparedSelection.locationKey);
        const initialAttrs = applyPredesignSizeAttrs({
            ...(preparedSelection.initialAttrs || {}),
            color2: "",
            color3: "",
        }, sizeTier, preparedSelection?.predesign);
        const normalizedColorIds = Array.isArray(colorIds)
            ? colorIds.map((value) => String(value || "").trim())
            : [];
        const firstColorId = String(normalizedColorIds[0] || "");
        const secondColorId = String(normalizedColorIds[1] || "");
        if (firstColorId) {
            initialAttrs.color1 = firstColorId;
            initialAttrs.color = firstColorId;
        }
        if (secondColorId) {
            initialAttrs.color2 = secondColorId;
        }

        // Escudo de POSICIÓN FIJA (fixed_placement): centro absoluto + alto fijo + bloqueado.
        // Lo definen los campos del escudo en el ABM; el front solo los aplica.
        const pd = preparedSelection.predesign || {};
        if (Number(pd.fixed_placement) === 1) {
            const absL = Number(pd.fixed_left_pct);
            const absT = Number(pd.fixed_top_pct);
            const fh = Number(pd.fixed_height_cm);
            if (Number.isFinite(absL)) initialAttrs.abs_left_pct = String(absL);
            if (Number.isFinite(absT)) initialAttrs.abs_top_pct = String(absT);
            if (Number.isFinite(fh) && fh > 0) {
                initialAttrs.fixed_height_cm = String(fh);
                initialAttrs.real_height_cm = String(fh);
            }
            // OJO: NO setear free_scale=1. El escudo tiene que seguir siendo "fixed predesign" para
            // que el render use el RECORTE OPACO del dibujo (no el lienzo 900² casi transparente, que
            // se dibuja diminuto/fuera de la máscara). El tamaño lo fuerza fixed_height_cm aparte.
            initialAttrs.print_locked = "1";    // sin mover ni redimensionar
            initialAttrs.offset_x_pct = "0";
            initialAttrs.offset_y_pct = "0";
        }

        dispatch({
            type: DESIGNER_ACTIONS.SET_PRINT_FILTER,
            partKey: normalizedLocationKey,
            filterName: "mode",
            value: "image",
        });
        cmdSetViewSide({
            dispatch,
            viewSide: Number(preparedSelection.side) === 1 ? "back" : "front",
        });
        const targetPrintKey = buildPrintInstanceKey(normalizedLocationKey);
        const inserted = await applySelectPrint(
            String(targetPrintKey),
            normalizedLocationKey,
            preparedSelection.predesign,
            preparedSelection.side,
            initialAttrs
        );
        return inserted?.printKey || null;
    }, [applySelectPrint]);

    const fetchColors = useCallback(async (partKey, materialId) => {
        if (!partKey || !materialId) return;
        const key = `${partKey}-${materialId}`;
        const materialKey = String(materialId);

        if (Object.prototype.hasOwnProperty.call(colorOptions, materialKey)) {
            setExpandedPalettes((prev) => ({ ...prev, [key]: true }));
            return;
        }

        setLoadingColors((prev) => ({ ...prev, [key]: true }));
        try {
            await ensureColorsForMaterial(materialKey);
            setExpandedPalettes((prev) => ({ ...prev, [key]: true }));
        } finally {
            setLoadingColors((prev) => ({ ...prev, [key]: false }));
        }
    }, [colorOptions, ensureColorsForMaterial]);

    const togglePalette = useCallback((partKey, materialId) => {
        if (!partKey || !materialId) return;
        const key = `${partKey}-${materialId}`;
        setExpandedPalettes((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const handleTogglePart = useCallback((partKey, selected) => {
        cmdTogglePart({
            dispatch,
            partKey,
            selected,
            treeContext,
        });
    }, [treeContext]);

    const resolvePairLink = useCallback((partKey, explicitMirrorPartKey = null) => {
        if (explicitMirrorPartKey === false) return null;
        if (explicitMirrorPartKey) return String(explicitMirrorPartKey);
        const partCard = partMaterialCardByKey[String(partKey)] || null;
        if (!partCard?.pairedPartKey || !partCard?.pairGroupKey) return null;
        const enabled = dualPaintByPair[partCard.pairGroupKey] !== false;
        return enabled ? String(partCard.pairedPartKey) : null;
    }, [partMaterialCardByKey, dualPaintByPair]);

    const isMaterialAllowedForPart = useCallback((partKey, materialId) => {
        const part = partCards.find((item) => String(item.key) === String(partKey));
        if (!part?.node) return false;
        const source = Array.isArray(part.node.materials) ? part.node.materials : Object.values(part.node.materials || {});
        const allowedMaterials = filterMaterialsForPart(part.label, source);
        return allowedMaterials.some((material) => String(material.id) === String(materialId));
    }, [partCards]);

    const handleSelectMaterial = useCallback((partKey, materialId, options = {}) => {
        if (!materialId) return;
        if (!isMaterialAllowedForPart(partKey, materialId)) return;
        cmdSelectMaterial({ dispatch, partKey, materialId, treeContext });

        const mirrorPartKey = resolvePairLink(partKey, options?.mirrorPartKey ?? null);
        if (!mirrorPartKey || String(mirrorPartKey) === String(partKey)) return;
        if (!isMaterialAllowedForPart(mirrorPartKey, materialId)) return;
        cmdSelectMaterial({ dispatch, partKey: mirrorPartKey, materialId, treeContext });
    }, [isMaterialAllowedForPart, resolvePairLink, treeContext]);

    const handleSelectColor = useCallback((partKey, materialId, colorObj, options = {}) => {
        const normalizedColor = normalizeColorObject(colorObj);
        const affectedPartKeys = [String(partKey)];
        cmdSelectColor({
            dispatch,
            partKey,
            materialId,
            colorObj: normalizedColor,
            treeContext,
        });

        const mirrorPartKey = resolvePairLink(partKey, options?.mirrorPartKey ?? null);
        if (mirrorPartKey && String(mirrorPartKey) !== String(partKey)) {
            const mirrorMaterialId = partMaterialCardByKey[String(mirrorPartKey)]?.selectedMaterialId || materialId;
            if (mirrorMaterialId && isMaterialAllowedForPart(mirrorPartKey, mirrorMaterialId)) {
                cmdSelectMaterial({ dispatch, partKey: mirrorPartKey, materialId: mirrorMaterialId, treeContext });
                cmdSelectColor({
                    dispatch,
                    partKey: mirrorPartKey,
                    materialId: mirrorMaterialId,
                    colorObj: normalizedColor,
                    treeContext,
                });
                affectedPartKeys.push(String(mirrorPartKey));
            }
        }
        setPartSparkleFx({
            keys: Array.from(new Set(affectedPartKeys)),
            token: Date.now(),
        });
    }, [partMaterialCardByKey, isMaterialAllowedForPart, resolvePairLink, treeContext]);

    // COLOR BASE: pinta TODAS las partes con material del color elegido (matcheando el mismo
    // color en la paleta de cada parte; si no está, usa el color base tal cual). Después el
    // usuario corrige el color de las partes puntuales con el picker por-parte de siempre.
    const handleApplyBaseColor = useCallback((colorObj) => {
        if (!colorObj) return;
        const norm = normalizeColorObject(colorObj);
        const code = String(norm?.code || norm?.hex || "").toLowerCase();
        (partMaterialCards || []).forEach((part) => {
            if (!part?.selected) return;
            const partKey = String(part.key);
            const materialId = part.selectedMaterialId ? String(part.selectedMaterialId) : null;
            if (!materialId) return;
            // Las partes sublimadas / con fullprint no llevan color plano → no se tocan.
            if (state.partSublimation?.[partKey] || state.selectedFullprints?.[partKey]) return;
            const avail = Array.isArray(part.availableColors) ? part.availableColors : [];
            const match = avail.find((c) => String(c?.code || c?.hex || "").toLowerCase() === code) || norm;
            cmdSelectColor({ dispatch, partKey, materialId, colorObj: normalizeColorObject(match), treeContext });
        });
        setBaseColor(norm);
    }, [partMaterialCards, state.partSublimation, state.selectedFullprints, treeContext]);

    const handleClearColor = useCallback((partKey, materialId, options = {}) => {
        cmdClearColor({ dispatch, partKey, materialId });
        const mirrorPartKey = resolvePairLink(partKey, options?.mirrorPartKey ?? null);
        if (!mirrorPartKey || String(mirrorPartKey) === String(partKey)) return;
        const mirrorMaterialId = partMaterialCardByKey[String(mirrorPartKey)]?.selectedMaterialId || materialId;
        if (!mirrorMaterialId) return;
        cmdClearColor({ dispatch, partKey: mirrorPartKey, materialId: mirrorMaterialId });
    }, [partMaterialCardByKey, resolvePairLink]);

    const handlePreviewPaintPart = useCallback(async (payload = {}) => {
        const partKey = String(payload?.partKey || "");
        if (!partKey) return;
        const partCard = partMaterialCardByKey[partKey] || null;
        if (!partCard) return;

        const owningGroup = (partGroups || []).find((group) =>
            Array.isArray(group.options) && group.options.some((option) => String(option.key) === partKey)
        );
        if (owningGroup?.key) {
            setActivePartSectionKey(String(owningGroup.key));
        }
        cmdSetActivePart({ dispatch, partKey });

        const preferredMaterialId = String(partCard.selectedMaterialId || partCard.materialOptions?.[0]?.id || "");
        if (!preferredMaterialId) return;
        handleSelectMaterial(partKey, preferredMaterialId, { mirrorPartKey: false });
        const colors = await ensureColorsForMaterial(preferredMaterialId);
        if (!Array.isArray(colors) || !colors.length) return;

        setColorQuickPicker({
            partKey,
            materialId: preferredMaterialId,
            mirrorPartKey: false,
            title: payload?.partLabel ? `Pintar ${payload.partLabel}` : `Pintar ${partCard.label || "parte"}`,
            subtitle: "Elegí un color y seguimos con la siguiente parte.",
            colors,
            selectedColorId: partCard.selectedColor?.id ?? partCard.selectedColor?.color_id ?? null,
            selectedColor: partCard.selectedColor || null,
        });
    }, [partMaterialCardByKey, partGroups, dispatch, handleSelectMaterial, ensureColorsForMaterial]);

    const handleQuickPickerSelectColor = useCallback((color) => {
        if (colorQuickPicker?.mode === "custom-cut-zone" && colorQuickPicker?.zoneId) {
            const resolvedZoneId = String(colorQuickPicker.zoneId);
            const mirroredZoneId = resolveMirroredCustomCutZoneId(resolvedZoneId);
            const normalizedColor = normalizeColorObject(color);
            setCustomCutZoneColors((prev) => {
                const next = {
                    ...(prev || {}),
                    [resolvedZoneId]: normalizedColor,
                };
                if (mirroredZoneId) {
                    next[mirroredZoneId] = normalizedColor;
                }
                return next;
            });
            setCustomCutSparkleFx({
                ids: mirroredZoneId ? [resolvedZoneId, mirroredZoneId] : [resolvedZoneId],
                token: Date.now(),
            });
            setColorQuickPicker(null);
            return;
        }
        if (!colorQuickPicker?.partKey || !colorQuickPicker?.materialId) {
            setColorQuickPicker(null);
            return;
        }
        handleSelectColor(
            colorQuickPicker.partKey,
            colorQuickPicker.materialId,
            color,
            { mirrorPartKey: colorQuickPicker.mirrorPartKey ?? null }
        );
        setColorQuickPicker(null);
    }, [colorQuickPicker, handleSelectColor]);

    const updateStripeDrawingsForSide = useCallback((side, updater) => {
        const normalizedSide = side === "back" ? "back" : "front";
        setStripeDrawingsBySide((prev) => {
            const current = Array.isArray(prev?.[normalizedSide]) ? prev[normalizedSide] : [];
            const nextForSide = typeof updater === "function" ? updater(current) : updater;
            const mirroredState = buildMirroredStripeState(normalizedSide, nextForSide, prev);
            return {
                ...(prev || {}),
                front: mirroredState.front,
                back: mirroredState.back,
            };
        });
    }, []);

    const clearStripeDrawingsForSide = useCallback((side) => {
        updateStripeDrawingsForSide(side, []);
    }, [updateStripeDrawingsForSide]);

    const popStripeDrawingForSide = useCallback((side) => {
        updateStripeDrawingsForSide(side, (prev) => (Array.isArray(prev) ? prev.slice(0, -1) : []));
    }, [updateStripeDrawingsForSide]);

    const updateCustomCutsForSide = useCallback((side, updater) => {
        const normalizedSide = side === "back" ? "back" : "front";
        setCustomCutsBySide((prev) => {
            const current = Array.isArray(prev?.[normalizedSide]) ? prev[normalizedSide] : [];
            const nextForSide = typeof updater === "function" ? updater(current) : updater;
            const normalizedCuts = (Array.isArray(nextForSide) ? nextForSide : [])
                .map((cut) => normalizeCustomCutStroke(cut))
                .filter((cut) => Array.isArray(cut.points_norm) && cut.points_norm.length >= 2)
                .slice(0, MAX_CUSTOM_CUTS_PER_SIDE);
            const mirroredState = buildMirroredCustomCutState(normalizedSide, normalizedCuts, prev);
            return {
                ...(prev || {}),
                front: mirroredState.front,
                back: mirroredState.back,
            };
        });
    }, []);

    const clearCustomCutsForSide = useCallback((side) => {
        updateCustomCutsForSide(side, []);
    }, [updateCustomCutsForSide]);

    const popCustomCutForSide = useCallback((side) => {
        updateCustomCutsForSide(side, (prev) => (Array.isArray(prev) ? prev.slice(0, -1) : []));
    }, [updateCustomCutsForSide]);

    const handleCustomCutZoneColorChange = useCallback((zoneId, color) => {
        if (!zoneId || !color) return;
        const resolvedZoneId = String(zoneId);
        const mirroredZoneId = resolveMirroredCustomCutZoneId(resolvedZoneId);
        const normalizedColor = normalizeColorObject(color);
        setCustomCutZoneColors((prev) => {
            const next = {
                ...(prev || {}),
                [resolvedZoneId]: normalizedColor,
            };
            if (mirroredZoneId) {
                next[mirroredZoneId] = normalizedColor;
            }
            return next;
        });
        setCustomCutSparkleFx({
            ids: mirroredZoneId ? [resolvedZoneId, mirroredZoneId] : [resolvedZoneId],
            token: Date.now(),
        });
    }, []);

    const handleCustomCutZoneClear = useCallback((zoneId) => {
        if (!zoneId) return;
        const resolvedZoneId = String(zoneId);
        const mirroredZoneId = resolveMirroredCustomCutZoneId(resolvedZoneId);
        setCustomCutZoneColors((prev) => {
            const next = { ...(prev || {}) };
            delete next[resolvedZoneId];
            if (mirroredZoneId) {
                delete next[mirroredZoneId];
            }
            return next;
        });
    }, []);

    const handleCustomCutRuntimeUpdate = useCallback((side, zones, runtime = null) => {
        const sideKey = side === "back" ? "back" : "front";
        customCutRuntimeBySideRef.current = {
            ...(customCutRuntimeBySideRef.current || {}),
            [sideKey]: runtime || null,
        };
        const nextZones = buildLightweightCustomCutZones(zones);
        setCustomCutZonesBySide((prev) => {
            const currentZones = Array.isArray(prev?.[sideKey]) ? prev[sideKey] : [];
            if (areLightweightCustomZonesEqual(currentZones, nextZones)) {
                return prev;
            }
            return {
                ...(prev || {}),
                [sideKey]: nextZones,
            };
        });
    }, []);

    const handlePreviewPaintCustomCutZone = useCallback((payload = {}) => {
        const zoneId = String(payload?.zoneId || "");
        if (!zoneId) return;
        const availableColors = Array.isArray(allColors) ? allColors : [];
        setActivePartSectionKey("group-cuts");
        setColorQuickPicker({
            mode: "custom-cut-zone",
            zoneId,
            title: payload?.zoneLabel ? `Pintar ${payload.zoneLabel}` : "Pintar recorte",
            subtitle: `Aplicá color sobre ${resolveCustomCutTargetGroupLabel(payload?.targetGroup)} y seguimos.`,
            colors: availableColors,
            selectedColorId: payload?.selectedColor?.id ?? payload?.selectedColor?.color_id ?? null,
            selectedColor: payload?.selectedColor || null,
        });
    }, [allColors]);

    const isDualPaintEnabled = useCallback((option) => {
        if (!option?.pairGroupKey || !option?.pairedPartKey) return false;
        return dualPaintByPair[String(option.pairGroupKey)] !== false;
    }, [dualPaintByPair]);

    const handleToggleDualPaint = useCallback((option, enabled) => {
        if (!option?.pairGroupKey) return;
        const pairGroupKey = String(option.pairGroupKey);
        setDualPaintByPair((prev) => ({ ...prev, [pairGroupKey]: !!enabled }));
    }, []);

    const handleToggleSublimation = useCallback((partKey) => {
        dispatch({ type: DESIGNER_ACTIONS.TOGGLE_SUBLIMATION, partKey, enabled: false });
        dispatch({ type: DESIGNER_ACTIONS.SELECT_FULLPRINT, partKey, fullprint: null });
    }, []);

    const handleSelectFullprint = useCallback((partKey) => {
        dispatch({ type: DESIGNER_ACTIONS.SELECT_FULLPRINT, partKey, fullprint: null });
    }, []);

    const handleSetPrintFilter = useCallback((partKey, filterName, value) => {
        cmdSetPrintFilter({
            dispatch,
            partKey,
            filterName,
            value,
        });
    }, []);

    const handleSetPrintMode = useCallback((printKey, mode) => {
        cmdSetPrintMode({
            dispatch,
            printKey,
            mode,
            selectedPrints: state.selectedPrints,
        });
    }, [state.selectedPrints]);

    const handleSelectPrintText = useCallback((partKey, text) => {
        cmdSetPrintText({
            dispatch,
            partKey,
            text,
        });
    }, []);

    const handleClearPrint = useCallback((printKey) => {
        cmdClearPrint({
            dispatch,
            printKey,
            selectedPrints: state.selectedPrints,
        });
    }, [state.selectedPrints]);

    const handleSetPrintAttrs = useCallback((partKey, attrs) => {
        if (!attrs || typeof attrs !== "object") return;
        const nextAttrs = { ...attrs };
        const currentSelection = state.selectedPrints?.[String(partKey)] || null;

        // free_scale=1 → el cliente lo agrandó a mano: NO forzar el tamaño fijo del tier
        // (si no, cada escritura de attrs le revierte el tamaño al inicial).
        const isFreeScaled = String(nextAttrs.free_scale ?? currentSelection?.attrs?.free_scale ?? "") === "1";
        if (!isFreeScaled && isFixedPredesignSelection(currentSelection)) {
            const normalizedPredesignAttrs = applyPredesignSizeAttrs(
                {
                    ...(currentSelection || {}),
                    attrs: { ...(currentSelection?.attrs || {}), ...nextAttrs },
                },
                nextAttrs.predesign_size_tier || currentSelection?.attrs?.predesign_size_tier
            );
            nextAttrs.predesign_size_tier = normalizedPredesignAttrs.predesign_size_tier;
            nextAttrs.scale_pct = normalizedPredesignAttrs.scale_pct;
        }

        if (Object.prototype.hasOwnProperty.call(nextAttrs, "rotation_deg")) {
            const parsed = Number(nextAttrs.rotation_deg);
            if (Number.isFinite(parsed)) {
                const normalized = ((parsed % 360) + 360) % 360;
                nextAttrs.rotation_deg = String(Math.max(0, Math.min(360, normalized)));
            }
        }

        cmdMergePrintAttrs({ dispatch, partKey, attrs: nextAttrs, context: treeContext });
    }, [state.selectedPrints, treeContext]);

    const handleTransformPrint = useCallback((partKey, attrs, meta = null) => {
        if (!attrs || typeof attrs !== "object") return;
        const phase = String(meta?.phase || "").toLowerCase();
        const canReassignByProximity = phase === "end";
        const selection = state.selectedPrints?.[String(partKey)] || null;
        if (!selection?.print) {
            handleSetPrintAttrs(partKey, attrs);
            return;
        }

        const currentLocationKey = String(getPrintLocationKeyFromSelection(partKey, selection));
        const currentSpot = printHotspotMetrics.map[currentLocationKey] || null;
        if (!currentSpot) {
            handleSetPrintAttrs(partKey, attrs);
            return;
        }

        const parsedDx = Number(attrs.offset_x_pct);
        const parsedDy = Number(attrs.offset_y_pct);
        const dx = Number.isFinite(parsedDx) ? parsedDx : Number(selection?.attrs?.offset_x_pct || 0) || 0;
        const dy = Number.isFinite(parsedDy) ? parsedDy : Number(selection?.attrs?.offset_y_pct || 0) || 0;
        const absoluteLeft = currentSpot.leftPct + dx;
        const absoluteTop = currentSpot.topPct + dy;

        if (!canReassignByProximity) {
            handleSetPrintAttrs(partKey, attrs);
            return;
        }

        const sideHint = resolvePrintSelectionSide(selection, currentSpot.side || null);
        const hotspotCandidates = printHotspotMetrics.list.filter((spot) => {
            if (sideHint && spot.side && spot.side !== sideHint) return false;
            const occupancyKey = buildPrintOccupancyKey(spot.key, sideHint || spot.side || "front");
            const occupants = occupiedPrintHotspots.get(occupancyKey);
            if (occupants && Array.from(occupants).some((occupantKey) => String(occupantKey) !== String(partKey))) {
                return false;
            }
            return true;
        });
        if (!hotspotCandidates.length) {
            handleSetPrintAttrs(partKey, attrs);
            return;
        }

        let nearest = null;
        hotspotCandidates.forEach((spot) => {
            const distSq = ((spot.leftPct - absoluteLeft) ** 2) + ((spot.topPct - absoluteTop) ** 2);
            if (!nearest || distSq < nearest.distSq) {
                nearest = { spot, distSq };
            }
        });

        if (!nearest?.spot) {
            handleSetPrintAttrs(partKey, attrs);
            return;
        }

        const nearestSpot = nearest.spot;
        const currentDistSq = ((currentSpot.leftPct - absoluteLeft) ** 2) + ((currentSpot.topPct - absoluteTop) ** 2);
        const shouldSwitch =
            String(nearestSpot.key) !== String(currentLocationKey)
            && (currentDistSq - nearest.distSq) > (PRINT_NEAREST_HOTSPOT_MIN_SWITCH_PCT ** 2);

        if (!shouldSwitch) {
            handleSetPrintAttrs(partKey, attrs);
            return;
        }

        const nextAttrs = {
            ...attrs,
            location_key: String(nearestSpot.key),
            offset_x_pct: String((absoluteLeft - nearestSpot.leftPct).toFixed(2)),
            offset_y_pct: String((absoluteTop - nearestSpot.topPct).toFixed(2)),
        };
        handleSetPrintAttrs(partKey, nextAttrs);
    }, [state.selectedPrints, printHotspotMetrics, occupiedPrintHotspots, handleSetPrintAttrs]);

    const handlePrintClipWarning = useCallback(() => {
        const now = Date.now();
        if ((now - printClipAlertCooldownRef.current) < 1800) return;
        printClipAlertCooldownRef.current = now;
        Swal.fire({
            icon: "warning",
            title: "Diseño recortado por contorno",
            html: `
                <div style="text-align:left; line-height:1.45;">
                    <p style="margin:0;">La personalización tocó una zona fuera de la prenda y se recortó automáticamente.</p>
                </div>
            `,
            showCloseButton: true,
            confirmButtonText: "Entendido",
            confirmButtonColor: "#d54ab7",
        });
    }, []);

    // Observaciones del cliente en el paso final (lo que no se pudo hacer dentro de la herramienta).
    const [observations, setObservations] = useState("");

    const handleSaveTemplate = useDesignerSaveFlow({
        orderItemId,
        observations,
        resultTreeArray,
        blockingIssues,
        printDetails,
        selectedPrints: state.selectedPrints,
        appliedPartsPreview,
        bSide,
        customerId,
        productModelId,
        draftStorageKey,
        datasheetGuard,
        saveBlockedByExistingDatasheet,
        setDatasheetGuard,
        setSaving,
        setSaveMsg,
        normalizedRoot,
        selectedPartsForPreview,
        allColors,
        stripeDrawingsBySide,
        stripeStrokeSummary,
        customCutsBySide,
        customCutZoneColors,
        customCutZonesBySide,
        customCutRuntimeBySideRef,
        customCutSummary,
        setSketchImage,
        setSketchError,
        appliedPartsBySection,
        appliedPrintsBySection,
        garmentName: activeVariantName && activeVariantName !== "Sin variante seleccionada" ? activeVariantName : "Tu prenda",
        onBundleUploaded: (bundleResponse) => {
            const maybePdf = String(bundleResponse?.guide_url || bundleResponse?.guide?.filename || "").trim();
            if (maybePdf) setSummaryPdfUrl(maybePdf);
        },
    });

    const handleResetDesign = useDesignerResetFlow({
        normalizedRoot,
        treeContext,
        draftStorageKey,
        dispatch,
        setCurrentStep,
        setSaveMsg,
        setSketchImage,
        setSketchError,
        setSideConfirm,
        setPrintHotspotFilter,
        setShowPrintHotspots,
    });

    const handleResetDesignWithStripes = useCallback(async () => {
        setStripeDrawingsBySide({ front: [], back: [] });
        setStripeDrawEditor((prev) => ({ ...(prev || {}), enabled: false, tool: "none" }));
        setCustomCutsBySide({ front: [], back: [] });
        setCustomCutZoneColors({});
        setCustomCutEditor((prev) => ({ ...(prev || {}), enabled: false, tool: "none" }));
        await handleResetDesign();
    }, [handleResetDesign]);

    const activePartPrintLocations = useMemo(
        () =>
            selectActivePartPrintLocations({
                allPrintLocations,
                resolvedActivePartKey,
            }),
        [allPrintLocations, resolvedActivePartKey]
    );

    const step1PrintLocations = useMemo(() => {
        return activePartPrintLocations;
    }, [activePartPrintLocations]);

    const step2PrintLocations = useMemo(
        () =>
            selectStep2PrintLocations({
                allPrintLocations,
                printHotspotFilter,
                resolvedActivePartKey,
            }),
        [allPrintLocations, printHotspotFilter, resolvedActivePartKey]
    );

    useEffect(() => {
        if (currentStepKey !== STEP.PAINT) return;
        if (!step1PrintLocations.length) {
            if (state.activePrintKey) {
                dispatch({ type: DESIGNER_ACTIONS.SET_ACTIVE_PRINT, printKey: null });
            }
            return;
        }
        const currentLocation = getPrintLocationKeyFromSelection(state.activePrintKey, activePrintSelection);
        const belongsToPart = step1PrintLocations.some((location) => String(location.key) === currentLocation);
        if (belongsToPart) return;
        dispatch({ type: DESIGNER_ACTIONS.SET_ACTIVE_PRINT, printKey: null });
    }, [currentStepKey, step1PrintLocations, state.activePrintKey, state.selectedPrints, activePrintSelection]);

    useEffect(() => {
        if (currentStepKey !== STEP.PRINTS) return;
        if (!step2PrintLocations.length) {
            if (state.activePrintKey) {
                dispatch({ type: DESIGNER_ACTIONS.SET_ACTIVE_PRINT, printKey: null });
            }
            return;
        }
        const currentLocation = getPrintLocationKeyFromSelection(state.activePrintKey, activePrintSelection);
        const existsInVisible = step2PrintLocations.some((location) => String(location.key) === currentLocation);
        if (existsInVisible) return;
        // No deseleccionar un print VÁLIDO (existente, en una ubicación real) solo porque su ubicación
        // quedó fuera del filtro de "parte activa": el usuario lo seleccionó tocándolo sobre la prenda
        // y ahí quiere ajustar tamaño. Solo limpiamos selecciones stale (print ya inexistente).
        const existsAtAll = allPrintLocations.some((location) => String(location.key) === currentLocation);
        if (existsAtAll && activePrintSelection) return;
        dispatch({ type: DESIGNER_ACTIONS.SET_ACTIVE_PRINT, printKey: null });
    }, [currentStepKey, step2PrintLocations, allPrintLocations, state.activePrintKey, state.selectedPrints, activePrintSelection]);

    useEffect(() => {
        if (currentStepKey === STEP.PRINTS) return;
        if (!showPrintHotspots && !printHotspotFilter) return;
        setShowPrintHotspots(false);
        setPrintHotspotFilter(null);
    }, [currentStepKey, showPrintHotspots, printHotspotFilter]);

    // Fuera del paso de tiras, desactivar la herramienta de dibujo para no trazar tiras por accidente.
    useEffect(() => {
        if (currentStepKey === STEP.STRIPES) return;
        if (!stripeDrawEditor?.enabled) return;
        setStripeDrawEditor((prev) => ({ ...(prev || {}), enabled: false, tool: "none" }));
    }, [currentStepKey, stripeDrawEditor?.enabled]);

    // Al terminar de dibujar UNA tira (sube el conteo del lado), fijarla: la herramienta vuelve a
    // modo selección para poder tocarla (color/borrar). Para otra tira, "Agregar tira" de nuevo.
    const prevStripeRef = useRef({ side: "front", count: 0 });
    useEffect(() => {
        if (currentStepKey !== STEP.STRIPES) return;
        const side = state.viewSide;
        const count = Array.isArray(stripeDrawingsBySide?.[side]) ? stripeDrawingsBySide[side].length : 0;
        const prev = prevStripeRef.current;
        if (prev.side === side && count > prev.count && stripeDrawEditor?.enabled && stripeDrawEditor?.tool !== "none") {
            setStripeDrawEditor((p) => ({ ...(p || {}), enabled: false, tool: "none" }));
        }
        prevStripeRef.current = { side, count };
    }, [currentStepKey, state.viewSide, stripeDrawingsBySide, stripeDrawEditor?.enabled, stripeDrawEditor?.tool]);

    useEffect(() => () => {
        if (printColorQuickSetupResolverRef.current) {
            printColorQuickSetupResolverRef.current(null);
            printColorQuickSetupResolverRef.current = null;
        }
    }, []);

    const goNext = useCallback(async () => {
        const transition = transitionWizardStep({
            currentStep,
            event: WIZARD_EVENTS.NEXT,
            totalSteps: steps.length,
            blockReason: stepBlockReason,
        });
        if (transition.blocked) {
            const blockMessages = stepBlockingMessages.length > 0
                ? stepBlockingMessages
                : [transition.blockReason || "Completá los datos requeridos."];
            setSaveMsg(blockMessages[0]);
            await Swal.fire({
                icon: "warning",
                title: "Antes de continuar",
                html: buildBlockingListHtml(stepBlockingBySection.length
                    ? stepBlockingBySection
                    : [{ title: "General", items: blockMessages }]
                ),
                confirmButtonText: "Entendido",
                confirmButtonColor: "#0071ce",
            });
            return;
        }
        setSaveMsg(null);
        setCurrentStep(transition.step);
    }, [currentStep, steps.length, stepBlockReason, stepBlockingMessages, stepBlockingBySection]);

    const goPrev = useCallback(() => {
        const transition = transitionWizardStep({
            currentStep,
            event: WIZARD_EVENTS.PREV,
            totalSteps: steps.length,
        });
        setSaveMsg(null);
        setCurrentStep(transition.step);
    }, [currentStep, steps.length]);

    const handleSelectPreviewPrint = useCallback((partKey) => {
        cmdSetActivePrint({ dispatch, printKey: String(partKey) });
    }, [dispatch]);

    const handleSelectActivePart = useCallback((partKey) => {
        cmdSetActivePart({ dispatch, partKey });
    }, [dispatch]);

    const handleSelectPrintLocation = useCallback((printKey) => {
        cmdSetActivePrint({ dispatch, printKey });
    }, [dispatch]);

    const handleSelectPrintInstance = useCallback((printKey) => {
        cmdSetActivePrint({ dispatch, printKey: String(printKey) });
    }, [dispatch]);

    const handleSelectPrintImage = useCallback((partKey, imageUrl, imageMode) => {
        cmdSetPrintImage({
            dispatch,
            partKey,
            imageUrl,
            imageMode,
        });
        const selection = state.selectedPrints?.[String(partKey)] || null;
        const printKind = String(selection?.attrs?.print_kind || "").toLowerCase();
        if (printKind === PRINT_KIND.PREDESIGN) return;
        handleSetPrintAttrs(partKey, {
            print_kind: PRINT_KIND.IMAGE_UPLOAD,
            resolved_master_print_type: String(MASTER_PRINT_TYPE.SERIGRAPHY),
            resolved_technique: "estampado",
            technique_locked: "1",
        });
    }, [dispatch, state.selectedPrints, handleSetPrintAttrs]);

    const handleSelectPrintFont = useCallback((partKey, fontId) => {
        cmdSetPrintFont({ dispatch, partKey, fontId });
    }, [dispatch]);

    const handleSelectPrintColor = useCallback((partKey, colorObj, colorIndex) => {
        cmdSetPrintColor({
            dispatch,
            partKey,
            colorObj,
            colorIndex,
        });
    }, [dispatch]);

    const handleToggleView = useCallback(() => {
        cmdSetViewSide({
            dispatch,
            viewSide: state.viewSide === "back" ? "front" : "back",
        });
    }, [dispatch, state.viewSide]);

    const sharedPrintSectionProps = useMemo(() => ({
        activePrintKey: state.activePrintKey,
        selectedPrints: state.selectedPrints,
        onSelectPrintLocation: handleSelectPrintLocation,
        printFilters: state.printFilters,
        onSetPrintFilter: handleSetPrintFilter,
        selectedPrint: activePrintSelection,
        selectedPrintsForLocation: allPrintSelections,
        printDetails,
        allColors,
        onSelectPrint: handleSelectPrint,
        onAddPrint: handleAddPrint,
        onAddTextPrint: handleAddTextPrintDirect,
        onAddImagePrint: handleAddImagePrint,
        onOpenLittleDesigns: loadLittlePredesigns,
        littlePredesigns,
        littlePredesignsLoading,
        littlePredesignsLoaded,
        onPrepareLittleDesign: handlePrepareLittleDesign,
        onApplyLittleDesign: handleApplyLittleDesign,
        onSelectPrintInstance: handleSelectPrintInstance,
        onClearPrint: handleClearPrint,
        onSetPrintMode: handleSetPrintMode,
        onSelectPrintText: handleSelectPrintText,
        onSelectPrintImage: handleSelectPrintImage,
        onSelectPrintFont: handleSelectPrintFont,
        onSelectPrintColor: handleSelectPrintColor,
        onSetPrintAttr: handleSetPrintAttrs,
        fontBaseUrl,
        designFontsLoaded,
        printValidationErrors: activePrintErrors,
        locationSublimationByKey,
        printAdvisoryMessage: printAdvisory,
        legalImageDisclaimer: IMAGE_LEGAL_DISCLAIMER,
        garmentDefaultMaterialName: garmentDefaultMaterialName,
    }), [
        state.activePrintKey,
        state.selectedPrints,
        state.printFilters,
        handleSelectPrintLocation,
        handleSetPrintFilter,
        activePrintSelection,
        allPrintSelections,
        printDetails,
        allColors,
        handleSelectPrint,
        handleAddPrint,
        handleAddImagePrint,
        loadLittlePredesigns,
        littlePredesigns,
        littlePredesignsLoading,
        littlePredesignsLoaded,
        designFontsLoaded,
        handlePrepareLittleDesign,
        handleApplyLittleDesign,
        handleSelectPrintInstance,
        handleClearPrint,
        handleSetPrintMode,
        handleSelectPrintText,
        handleSelectPrintImage,
        handleSelectPrintFont,
        handleSelectPrintColor,
        handleSetPrintAttrs,
        fontBaseUrl,
        activePrintErrors,
        locationSublimationByKey,
        printAdvisory,
        garmentDefaultMaterialName,
    ]);

    const variantStepProps = useMemo(() => ({
        activeVariantName,
        variantOptions,
        switchingVariant,
        onContinue: () => setCurrentStep(2),
        onOpenCatalog: onSelectVariant ? () => setShowVariantCatalog(true) : null,
    }), [activeVariantName, variantOptions, switchingVariant, onSelectVariant]);

    const detailsStepProps = useMemo(() => ({
        groups: detailVariantGroups,
        onSelectVariant: (key) => handleTogglePart(key, true),
    }), [detailVariantGroups, handleTogglePart]);

    const stripesStepProps = useMemo(() => ({
        stripeColors: Array.isArray(stripeWizard?.colors) ? stripeWizard.colors : [],
        activeColor: stripeDrawEditor?.color || "#1d4ed8",
        onSetActiveColor: (code) => setStripeDrawEditor((prev) => ({ ...(prev || {}), color: String(code || "#1d4ed8") })),
        drawArmed: !! stripeDrawEditor?.enabled && stripeDrawEditor?.tool !== "none",
        drawTool: stripeDrawEditor?.tool || "none",
        onArm: (tool) => setStripeDrawEditor((prev) => ({ ...(prev || {}), enabled: true, tool: tool === "curve" ? "curve" : "line" })),
        onDisarm: () => setStripeDrawEditor((prev) => ({ ...(prev || {}), enabled: false, tool: "none" })),
        strokeCount: Array.isArray(stripeDrawingsBySide?.[state.viewSide]) ? stripeDrawingsBySide[state.viewSide].length : 0,
        onFinish: goNext,
    }), [
        stripeWizard,
        stripeDrawEditor,
        stripeDrawingsBySide,
        state.viewSide,
        goNext,
    ]);

    const step1PartsSectionProps = useMemo(() => ({
        ...sharedPrintSectionProps,
        stepMode: "part-material",
        includeStripeSections: false,
        partCards: filteredPartMaterialCards,
        partGroups,
        activePartKey: resolvedActivePartKey,
        activeSectionKey: activePartSectionKey,
        onChangeActiveSection: setActivePartSectionKey,
        onSelectActivePart: handleSelectActivePart,
        onTogglePart: handleTogglePart,
        hideSearch: true,
        onSelectMaterial: handleSelectMaterial,
        onFetchColors: fetchColors,
        onTogglePalette: togglePalette,
        onSelectColor: handleSelectColor,
        onClearColor: handleClearColor,
        onApplyBaseColor: handleApplyBaseColor,
        baseColor,
        baseColorOptions: Array.isArray(allColors) ? allColors : [],
        isDualPaintEnabled,
        onToggleDualPaint: handleToggleDualPaint,
        onToggleSublimation: handleToggleSublimation,
        fullprints: [],
        onSelectFullprint: handleSelectFullprint,
        printLocations: step1PrintLocations,
        stripeWizard,
        customCutWizard,
        customCutEditor,
        onSetCustomCutEditor: setCustomCutEditor,
        customCutsBySide,
        customCutZones: customCutZonesBySide?.[state.viewSide] || [],
        customCutZoneColors,
        onSelectCustomCutZoneColor: handleCustomCutZoneColorChange,
        onClearCustomCutZoneColor: handleCustomCutZoneClear,
        onUndoCustomCut: popCustomCutForSide,
        onClearCustomCuts: clearCustomCutsForSide,
        stripeDrawEditor,
        onSetStripeDrawEditor: setStripeDrawEditor,
        stripeDrawingsBySide,
        viewSide: state.viewSide,
        onUndoStripeStroke: popStripeDrawingForSide,
        onClearStripeDrawings: clearStripeDrawingsForSide,
        onPreviewPaintPart: handlePreviewPaintPart,
        customCutSummary,
    }), [
        sharedPrintSectionProps,
        filteredPartMaterialCards,
        partGroups,
        resolvedActivePartKey,
        activePartSectionKey,
        handleSelectActivePart,
        handleTogglePart,
        handleSelectMaterial,
        fetchColors,
        togglePalette,
        handleSelectColor,
        handleClearColor,
        handleApplyBaseColor,
        baseColor,
        allColors,
        isDualPaintEnabled,
        handleToggleDualPaint,
        handleToggleSublimation,
        handleSelectFullprint,
        step1PrintLocations,
        stripeWizard,
        customCutWizard,
        customCutEditor,
        customCutsBySide,
        customCutZonesBySide,
        customCutZoneColors,
        handleCustomCutZoneColorChange,
        handleCustomCutZoneClear,
        popCustomCutForSide,
        clearCustomCutsForSide,
        stripeDrawEditor,
        stripeDrawingsBySide,
        state.viewSide,
        popStripeDrawingForSide,
        clearStripeDrawingsForSide,
        handlePreviewPaintPart,
        customCutSummary,
    ]);

    const step2PartsSectionProps = useMemo(() => ({
        ...sharedPrintSectionProps,
        stepMode: "print",
        printLocations: step2PrintLocations,
        categoryId: printCategoryId,
    }), [sharedPrintSectionProps, step2PrintLocations, printCategoryId]);

    const finalStepProps = useMemo(() => ({
        imageLegalDisclaimer: IMAGE_LEGAL_DISCLAIMER,
        garmentName: activeVariantName && activeVariantName !== "Sin variante seleccionada" ? activeVariantName : "Tu prenda",
        blockingIssues,
        appliedPartsBySection,
        appliedPrintsBySection,
        saveMsg,
        saveMsgLooksSuccess,
        saveBlockedByExistingDatasheet,
        datasheetGuardMessage: datasheetGuard.message,
        sketchGenerating,
        sketchError,
        sketchImage,
        finalPreviewImage,
        finalPreviewLoading,
        onSave: handleSaveTemplate,
        onBack: goPrev,
        saving,
        saveDisabled,
        summaryPdfUrl,
        stripeStrokeSummary,
        customCutSummary,
        observations,
        onObservationsChange: setObservations,
    }), [
        activeVariantName,
        goPrev,
        observations,
        blockingIssues,
        appliedPartsBySection,
        appliedPrintsBySection,
        saveMsg,
        saveMsgLooksSuccess,
        saveBlockedByExistingDatasheet,
        datasheetGuard.message,
        sketchGenerating,
        sketchError,
        sketchImage,
        finalPreviewImage,
        finalPreviewLoading,
        handleSaveTemplate,
        saving,
        saveDisabled,
        summaryPdfUrl,
        stripeStrokeSummary,
        customCutSummary,
    ]);

    if (!isDesignerOpen) return null;

    const content = (
        <DesignerMainLayout
            isPageVariant={isPageVariant}
            variantError={variantError}
            onClose={onClose}
            steps={steps}
            currentStep={currentStep}
            currentStepKey={currentStepKey}
            stripeColors={Array.isArray(stripeWizard?.colors) ? stripeWizard.colors : []}
            variantStepProps={variantStepProps}
            detailsStepProps={detailsStepProps}
            stripesStepProps={stripesStepProps}
            isPreviewCollapsed={isPreviewCollapsed}
            onTogglePreviewCollapsed={() => setIsPreviewCollapsed((prev) => !prev)}
            selectedPartsForPreview={selectedPartsForPreview}
            normalizedRoot={normalizedRoot}
            viewSide={state.viewSide}
            selectedPrints={state.selectedPrints}
            printDetails={printDetails}
            allColors={allColors}
            activePrintKey={state.activePrintKey}
            onSelectPreviewPrint={handleSelectPreviewPrint}
            onTransformPrint={handleTransformPrint}
            onDeletePrint={handleClearPrint}
            onEditPrintColor={handleSelectPrintColor}
            onPrintClipWarning={handlePrintClipWarning}
            partHotspots={partHotspots}
            hasBackView={hasBackView}
            onToggleView={handleToggleView}
            onResetDesign={handleResetDesignWithStripes}
            saving={saving}
            saveDisabled={saveDisabled}
            onPrev={goPrev}
            onNext={goNext}
            onSave={handleSaveTemplate}
            stripeDrawingsBySide={stripeDrawingsBySide}
            stripeDrawEditor={stripeDrawEditor}
            onSetStripeDrawEditor={setStripeDrawEditor}
            onUpdateStripeDrawingsBySide={updateStripeDrawingsForSide}
            customCutsBySide={customCutsBySide}
            customCutEditor={customCutEditor}
            onSetCustomCutEditor={setCustomCutEditor}
            onUpdateCustomCutsBySide={updateCustomCutsForSide}
            customCutZoneColors={customCutZoneColors}
            onCustomCutRuntimeUpdate={handleCustomCutRuntimeUpdate}
            onPreviewPaintCustomCutZone={handlePreviewPaintCustomCutZone}
            highlightedPartKey={colorQuickPicker?.partKey || null}
            sparklePartKeys={partSparkleFx.keys}
            sparkleToken={partSparkleFx.token}
            highlightedCustomCutZoneId={colorQuickPicker?.mode === "custom-cut-zone" ? colorQuickPicker.zoneId : null}
            sparkleCustomCutZoneIds={customCutSparkleFx.ids}
            sparkleCustomCutToken={customCutSparkleFx.token}
            step1PartsSectionProps={step1PartsSectionProps}
            step2PartsSectionProps={step2PartsSectionProps}
            finalStepProps={finalStepProps}
            isFooterNavCollapsed={isFooterNavCollapsed}
            onToggleFooterCollapse={() => setIsFooterNavCollapsed((prev) => !prev)}
        />
    );

    return (
        <>
            {isPageVariant ? (
                <section className={styles.pageShell}>
                    {content}
                </section>
            ) : (
                <div className={styles.modalBackground} onClick={onClose}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        {content}
                    </div>
                </div>
            )}

            <ConfirmSideModal
                sideConfirm={sideConfirm}
                applySelectPrint={applySelectPrint}
                setSideConfirm={setSideConfirm}
                setViewSide={(side) =>
                    cmdSetViewSide({ dispatch, viewSide: side })
                }
            />

            <DesignerVariantCatalogModal
                show={showVariantCatalog}
                variantOptions={variantOptions}
                switchingVariant={switchingVariant}
                onClose={() => setShowVariantCatalog(false)}
                onSelectVariant={onSelectVariant ? (item) => {
                    // Elegir un modelo PROPUESTO sale del modo personalizado y resetea el opt-in.
                    setDesignMode("proposed");
                    setCustomCutOptIn(false);
                    return onSelectVariant(item);
                } : null}
                onSelectCustom={() => {
                    setDesignMode("custom");
                    setShowVariantCatalog(false);
                }}
                isCustomActive={designMode === "custom"}
                fallbackLogo={mcbRayitoLogo}
            />

            {printColorQuickSetup ? (
                <PrintColorQuickSetupModal
                    title={printColorQuickSetup.title}
                    subtitle="Elegí los colores del diseño antes de insertarlo."
                    allColors={allColors}
                    colorSlots={printColorQuickSetup.colorSlots}
                    initialColorIds={printColorQuickSetup.initialColorIds}
                    onClose={() => {
                        const resolver = printColorQuickSetupResolverRef.current;
                        printColorQuickSetupResolverRef.current = null;
                        setPrintColorQuickSetup(null);
                        resolver?.(null);
                    }}
                    onApply={(selectedColors) => {
                        const resolver = printColorQuickSetupResolverRef.current;
                        printColorQuickSetupResolverRef.current = null;
                        setPrintColorQuickSetup(null);
                        resolver?.(Array.isArray(selectedColors) ? selectedColors : []);
                    }}
                />
            ) : null}
            <DesignerColorQuickPickerModal
                isOpen={!!colorQuickPicker}
                title={colorQuickPicker?.title || "Elegí un color"}
                subtitle={colorQuickPicker?.subtitle || ""}
                colors={colorQuickPicker?.colors || []}
                selectedColor={colorQuickPicker?.selectedColor || null}
                selectedColorId={colorQuickPicker?.selectedColorId ?? null}
                onClose={() => setColorQuickPicker(null)}
                onSelectColor={handleQuickPickerSelectColor}
            />
        </>
    );
};

export default Designer;
